/**
 * DashboardBody — the shared dashboard render composition.
 *
 * Single source of truth for laying out a dashboard body (sections + KPI
 * strip + charts + tables + KPI-drill panel + row-action expansion), consumed
 * by BOTH the portal (GenericDashboardPage) and the builder Preview
 * (PreviewModal / RealDashboardCanvas) so they render EXACTLY the same. It is
 * a faithful lift of the portal's composition (the correct reference).
 *
 * The component OWNS all render + interaction state (drill toggle/cache, table
 * pagination/sort/search/fullscreen, row-drill swap) and takes an
 * injected `DashboardDataSource` (the per-app fetch adapter) — the seam is the
 * HTTP boundary, so behavior is shared and only the endpoint differs. It
 * consumes the FLATTENED engine-shape config (`DashboardRenderConfig`); the
 * builder adapts its authoring config via `flattenForEngine` first.
 *
 * Layout pipeline (uniform grid, review-hardened):
 *   * `bucketDashboardConfig` (shared with `DashboardSkeleton`) groups the
 *     config into sections and derives every item's key/width/height/order;
 *   * a memoized pass attaches the fetched data BY CONFIG INDEX (charts:
 *     `data[i]` pairs with `config.charts[i]` — the engine emits exactly one
 *     entry per config chart in config order, see the layouts memo; KPIs and
 *     texts were always index-matched) and runs the placement simulator per
 *     section — an invalid layout value FAILS LOUD as a per-section error
 *     card (never a clamp, never a freeze);
 *   * cells render through the memoized `GridCell`, in visual (row-major)
 *     order so DOM/tab order follows the layout.
 *
 * Table view state (page/sort/search) is LIFTED into a per-item store here,
 * so a table keeps its state across the desktop/mobile breakpoint remount.
 * The row-drill is a STATE OF THE CELL, shown one of two ways the author
 * chooses per report (`row_drill_mode`, see `row-drill-mode.ts`):
 * `"swap"` (default) hides the table face (kept MOUNTED — instant
 * restore, no refetch) and renders the panel in the same cell, with the
 * slide; `"accordion"` (opt-in) mounts `RowDrillPanel` in an expansion
 * row UNDER the clicked row, inside the table's own scrolling zone — the
 * row stays visible. Either way the grid is never re-simulated on
 * open/close and the cell never changes size.
 *
 * Inspector (builder-only): when `onSelectItem`/`selectedItemId` are passed,
 * each rendered item is wrapped in a clickable selection ring (the Dashboard
 * tab editor). The portal passes nothing → no ring, zero cost.
 */

import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ArrowLeft } from "lucide-react";

import { MOTION } from "@pervasivemind/bi-components";
import { useTranslation } from "react-i18next";

import { BlockErrorCard, BlockStale } from "./BlockStatus";
import { crossFilterActiveValue } from "./filter-write";
import { GlossaryDrawer } from "./GlossaryDrawer";
import { DataTable, type ColumnDef, type ColumnRender } from "./DataTable";
import { GenericChart } from "./GenericChart";
import { chartPropsFromConfig } from "./chart-props";
import { KNOWN_COLUMN_RENDERS, toColumnDefs } from "./table-columns";
import { KPICard } from "./KPICard";
import { FadeRiseIn, FadeSwap, NumberTween, RefreshOverlay } from "./motion/index";
import { parseFormattedNumber } from "./motion/formatted-number";
import { MessageCard } from "./MessageCard";
import { AiReadoutCard } from "./AiReadoutCard";
import { SavedViewsPortfolioCard } from "./SavedViewsPortfolioCard";
import { SectionHeader } from "./SectionHeader";
import { DrillSection } from "./DrillSection";
import { RowDrillPanel } from "./RowDrillPanel";
import { resolveRowDrillMode, type RowDrillMode } from "./row-drill-mode";
import {
  ItemFlag,
  PickPointBanner,
  RowFlagProvider,
  RowMark,
  rowIdentity,
  useRowFlags,
  type RowFlagAdapter,
} from "./row-flags";
import { ViewSlide } from "./motion/ViewSlide";
import { formatCellValue } from "./formatting";
import { resolveLocalized, type DisplayLabel } from "./i18n-resolve";
import { useLang } from "./i18n-binding";
import {
  MAIN_TABLE_ID,
  type DashboardDataSource,
  type TableExportFormat,
  type SavedViewRef,
} from "./dashboard-data";
import { withTableDataCache } from "./table-data-cache";
import {
  bucketDashboardConfig,
  byVisualOrder,
  type GridItemSpec,
  type SectionBucketSpec,
} from "./dashboard-buckets";
import {
  GAP_PX,
  GRID_COLS,
  ROW_UNIT_PX,
  cellPx,
  placeItems,
  stretchToFill,
  type Placement,
} from "./grid-layout";
import type {
  ChartConfig,
  ChartData,
  DashboardRenderConfig,
  DrillData,
  Kpi,
  KpiConfig,
  RowDrillData,
  TableConfig,
  TextValue,
  DashboardFilter,
  DrillLayout,
} from "./types";

/** The per-column cell render modes ``DataTable`` understands. A
 *  config value outside this set maps to undefined (default cell) —
 *  style-only degradation, never a broken cell. */

/** KPI variants whose ``value`` is a display-only string rendered as-is
 *  by the underlying bi-components widget (no internal numeric parsing
 *  of the prop). ONLY these may receive a ``NumberTween`` node — the
 *  numeric variants (ring / bar / anomaly / score / compare) derive
 *  ``numericValue`` from ``String(value)`` inside ``KPICard`` and would
 *  read ``[object Object]`` → 0 from a React element. */
const KPI_TWEEN_VARIANTS: ReadonlySet<string> = new Set([
  "card",
  "compact",
  "hero",
  "iconstat",
  "sparkline",
]);

/** Cheap stable signature of a chart's fetched payload — the FadeSwap
 *  key for the tier-1 chart transition (plan §2.4): the plot crossfades
 *  ONLY when the data actually changed, not on every refetch. djb2 over
 *  the JSON string keeps React keys short. */
function chartDataSig(chart: ChartData): string {
  const json = JSON.stringify([chart.type, chart.error ?? null, chart.data]);
  let h = 5381;
  for (let i = 0; i < json.length; i++) {
    h = ((h << 5) + h + json.charCodeAt(i)) | 0;
  }
  return String(h >>> 0);
}

const KPI_ACCENTS = [
  "border-red-500",
  "border-blue-500",
  "border-emerald-500",
  "border-purple-500",
  "border-amber-500",
  "border-cyan-500",
  "border-pink-500",
  "border-indigo-500",
] as const;

/** 1..12 col-span class (literal strings so Tailwind JIT picks them up).
 *  ONLY the KPI strip (untouched in the uniform-grid project) still uses
 *  this — the charts/tables grid now places with inline styles from the
 *  `grid-layout` simulator (the old `rowSpanClass` lookup died with it).
 *  Exported for `DashboardSkeleton`, which mirrors the strip layout. */
export function spanClass(w: number): string {
  const clamped = Math.max(1, Math.min(12, w));
  const map: Record<number, string> = {
    1: "md:col-span-1", 2: "md:col-span-2", 3: "md:col-span-3",
    4: "md:col-span-4", 5: "md:col-span-5", 6: "md:col-span-6",
    7: "md:col-span-7", 8: "md:col-span-8", 9: "md:col-span-9",
    10: "md:col-span-10", 11: "md:col-span-11", 12: "md:col-span-12",
  };
  return map[clamped] ?? "md:col-span-12";
}

/** KPI-strip uniform column classes (exported for `DashboardSkeleton`).
 *
 *  SIX gets a breakpoint pair, not a single class. A six-KPI strip is the
 *  "action queues" shape — six sibling worklists that are read ACROSS, so
 *  wrapping them 3+3 breaks the row into two half-thoughts. At `md`
 *  (768px) six tiles would be ~118px each, too narrow for a card that
 *  carries a value plus a caption, so the wrap survives there and the
 *  single row arrives at `xl` (1280px), where each tile still clears
 *  ~200px. Both classes are LITERALS in this string for the same reason
 *  the rest of the map is: the Tailwind JIT scans this source and cannot
 *  see a runtime-computed class name. */
export const UNIFORM_MD: Record<number, string> = {
  2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4",
  5: "md:grid-cols-5", 6: "md:grid-cols-3 xl:grid-cols-6", 7: "md:grid-cols-4",
  8: "md:grid-cols-4", 9: "md:grid-cols-3", 10: "md:grid-cols-5",
  11: "md:grid-cols-4", 12: "md:grid-cols-4",
};

/** Vertical gap between stacked dashboard sections — the ONE class every
 *  host that stacks this body's sections must use (PR-3 §1): the portal
 *  (`GenericDashboardPage`), the builder Preview (`PreviewModal`), the
 *  builder Dashboard-tab canvas (`RealDashboardCanvas`) and the shared
 *  `DashboardSkeleton`. Before this constant the three hosts diverged
 *  (portal 12px / PreviewModal 24px / canvas 16px) — one of the few spots
 *  where the builder↔portal mirror was NOT pixel-identical. The value is
 *  the PORTAL's historical `space-y-3` (12px): it's what end users see.
 *  Kept as a string LITERAL so the Tailwind JIT (which scans this package's
 *  source in both apps' content globs) generates the class. */
export const SECTION_STACK_CLASS = "space-y-3";

/** Inline style of the uniform grid container. INLINE STYLES, not
 *  dynamic Tailwind classes — the JIT can't see runtime-computed class
 *  names (documented repo gotcha), inline styles have no such trap.
 *  Exported for `DashboardSkeleton` so the loading blocks land on the
 *  exact same grid. */
export const GRID_CONTAINER_STYLE = {
  display: "grid",
  gridTemplateColumns: `repeat(${GRID_COLS}, minmax(0, 1fr))`,
  gridAutoRows: `${ROW_UNIT_PX}px`,
  gap: `${GAP_PX}px`,
} as const;

/** Desktop breakpoint — matches the old `md:` Tailwind prefix the
 *  12-col grid used (`md:grid-cols-12`). Below it the grid renders as
 *  a 1-column stack in authoring order (no simulator). */
const DESKTOP_MEDIA_QUERY = "(min-width: 768px)";

/** Track the md breakpoint with a live matchMedia listener. Defaults to
 *  desktop when `matchMedia` is unavailable (non-browser env — jsdom
 *  tests override it; SSR is not a consumer of this package).
 *  Exported for `DashboardSkeleton` (same breakpoint, same behavior). */
export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(DESKTOP_MEDIA_QUERY).matches
      : true,
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const onChange = (e: MediaQueryListEvent): void => setIsDesktop(e.matches);
    mql.addEventListener("change", onChange);
    setIsDesktop(mql.matches);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
}

/** Controlled row-drill contract between the grid host and a table: the
 *  host (`DashboardBody`) owns the fetch + state; the table renders the
 *  `RowDrillPanel` inside its own cell — under the row (accordion) or in
 *  place of its content (swap), per `row_drill_mode`. One shared
 *  definition for the table prop AND the host plumbing. */
export interface ExternalRowDrill {
  activeValue: string | null;
  /** The full clicked row — feeds the panel's context chips. */
  activeRow: Record<string, unknown> | null;
  data: RowDrillData | null;
  loading: boolean;
  error: string | null;
  onToggle: (rowValue: string, row: Record<string, unknown>) => void;
  onClose: () => void;
}

/** A table's lifted view state (review #2): pagination/sort/search live
 *  in `DashboardBody`'s per-item store so they survive the remounts
 *  caused by desktop↔mobile breakpoint crossings. (The old row-drill
 *  band re-parenting remount is gone — the swap keeps the table
 *  mounted — but the lift stays for the breakpoint case.) Fullscreen
 *  stays local — it is ephemeral chrome, not navigation state. */
export interface TableViewState {
  page: number;
  sortBy: string | null;
  sortDir: "asc" | "desc";
  search: string;
  /** Active quick-filter chip value ("" = all; absent in older state). */
  chip?: string;
}

// ---------------------------------------------------------------------------
// Internal self-paginating table (lifted from the portal's NamedDataTable).
// Fetches via the injected data source. View state (page/sort/search) is
// either lifted to the parent (grid items — survives remounts) or internal
// (legacy singular table). Used for both named tables and the legacy singular
// table (via the MAIN_TABLE_ID sentinel).
// ---------------------------------------------------------------------------

interface DashboardTableProps {
  tableConfig: TableConfig;
  /** What the controls show (cell cross-filter highlight). */
  activeFilters: Record<string, string>;
  /** What the requests carry; `activeFilters` when absent. The portal
   *  passes the copy that waits for the user to stop clicking. */
  queryFilters?: Record<string, string>;
  data: DashboardDataSource;
  exportName: string;
  /** Chart configs for this table's row-drill target (drill chart styling). */
  drillChartConfigs?: ChartConfig[];
  nestedTableId?: string | null;
  nestedDrillTarget?: string | null;
  nestedDrillParam?: string | null;
  /** Cross-filter: host callback that sets/clears a report filter when
   * the user clicks a cell in a ``filter_key`` column. */
  onCrossFilter?: (filterKey: string, rawValue: string) => void;
  /** Filter keys eligible as cross-filter targets (select/autocomplete,
   * not locked). A column's ``filter_key`` outside this set is ignored. */
  crossFilterKeys?: ReadonlySet<string>;
  /** Uniform grid (desktop): stretch the card to fill its fixed grid
   * cell; rows scroll internally, pagination footer pinned. */
  fillCell?: boolean;
  /** Mobile stack: natural height capped at this px (internal scroll). */
  capPx?: number;
  /** Uniform grid (desktop) — row-drill state is CONTROLLED by the
   * parent (`DashboardBody`'s per-cell store). Absent → the table owns
   * the fetch itself (mobile stack + legacy singular table). Either
   * way the drill renders as the SAME in-place swap. */
  externalDrill?: ExternalRowDrill;
  /** Grid column span of the hosting cell — the drill panel's automatic
   * split/stack composition reads it (≥ 8 → side-by-side). Absent
   * (mobile / legacy) → stacked. */
  drillColSpan?: number;
  /** Optional composition override from the drill config
   * (``drill_layout``). */
  drillLayout?: DrillLayout | null;
  /** Lifted view state (review #2). When `onViewStateChange` is set the
   * table is CONTROLLED: `viewState` (or the config default while the
   * store has no entry yet) is the source of truth and every change
   * reports up. Absent → the historical internal state. */
  viewState?: TableViewState | null;
  onViewStateChange?: (next: TableViewState) => void;
}

function defaultTableView(tableConfig: TableConfig): TableViewState {
  return {
    page: 1,
    sortBy: null,
    sortDir: tableConfig.sort_dir === "asc" ? "asc" : "desc",
    search: "",
    chip: "",
  };
}

function DashboardTable({
  tableConfig,
  activeFilters,
  queryFilters,
  data,
  exportName,
  drillChartConfigs,
  nestedTableId,
  nestedDrillTarget,
  nestedDrillParam,
  onCrossFilter,
  crossFilterKeys,
  fillCell,
  capPx,
  externalDrill,
  drillColSpan,
  drillLayout,
  viewState,
  onViewStateChange,
}: DashboardTableProps): JSX.Element {
  const { t } = useTranslation();
  const lang = useLang();
  const tableId = tableConfig.id || MAIN_TABLE_ID;

  // View state — controlled (lifted store) or internal (legacy table).
  const controlled = onViewStateChange != null;
  const [internalView, setInternalView] = useState<TableViewState>(() =>
    defaultTableView(tableConfig),
  );
  const view = controlled
    ? (viewState ?? defaultTableView(tableConfig))
    : internalView;
  const { page, sortBy, sortDir, search } = view;
  // Quick-filter chip (DSA prototype v7 matrix): table-local like the
  // search, it reaches ONLY this table's SQL as {filter:<chips.key>}.
  const chipsCfg = tableConfig.chips ?? null;
  const chip = chipsCfg ? (view.chip ?? "") : "";
  const fetchFilters = queryFilters ?? activeFilters;
  const tableFilters = useMemo(
    () => (chipsCfg && chip ? { ...fetchFilters, [chipsCfg.key]: chip } : fetchFilters),
    [fetchFilters, chipsCfg, chip],
  );
  const updateView = (patch: Partial<TableViewState>): void => {
    const next = { ...view, ...patch };
    if (controlled) onViewStateChange(next);
    else setInternalView(next);
  };

  // column label/key → SQL column, so server-side sort addresses the right col.
  const labelToSqlCol = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of tableConfig.columns) {
      const stableKey = c.key || c.column?.replace(/^"|"$/g, "") || c.label;
      map.set(stableKey, c.column);
    }
    return map;
  }, [tableConfig.columns]);

  // A page this instance's data source already settled for the SAME
  // request (a tab switch remounted the table): paint it at once and
  // revalidate in the background instead of showing a loading body.
  const [cachedFirstPage] = useState(() =>
    data.peekTableData?.({
      tableId,
      filters: tableFilters,
      sortBy: sortBy ? (labelToSqlCol.get(sortBy) ?? sortBy) : undefined,
      sortDir,
      limit: tableConfig.page_size || 10,
      offset: (page - 1) * (tableConfig.page_size || 10),
      search: search.trim() || undefined,
    }),
  );
  const [rows, setRows] = useState<Array<Record<string, unknown>>>(
    () => cachedFirstPage?.rows ?? [],
  );
  const [total, setTotal] = useState(() => cachedFirstPage?.total_count ?? 0);
  // Pivot (DSA 1.20): the dynamic columns arrive with the rows.
  const [pivotCols, setPivotCols] = useState<Array<{ key: string; label: string }>>(
    () => cachedFirstPage?.pivot_columns ?? [],
  );
  const [loading, setLoading] = useState(false);
  /** Error of the LAST page fetch (null = healthy). Fail loud (rule 9):
   *  no rows to keep → BlockErrorCard in the slot; stale rows → BlockStale. */
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [retryTick, setRetryTick] = useState(0);
  /** SWR (PR-B): true once the FIRST fetch settled — from then on a
   *  ``loading`` window is a REFRESH (stale rows stay mounted under a
   *  ``RefreshOverlay`` dim) instead of a bare "Loading data…" body. */
  const hasSettledRef = useRef(cachedFirstPage != null);
  const [drillValue, setDrillValue] = useState<string | null>(null);
  const [drillRow, setDrillRow] = useState<Record<string, unknown> | null>(null);
  const [drillData, setDrillData] = useState<RowDrillData | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  /** Settled-response guard for the INTERNAL drill fetch (review #2b —
   *  mirror of the controlled path's guard in `toggleRowDrill`): only
   *  the latest REQUEST (a sequence id, not the row value — the same
   *  row re-requested under new filters is a different request, audit
   *  #195) may apply its response or clear the loading flag. Cleared on
   *  close/invalidations so a late settle can't resurrect a closed drill. */
  const drillReqRef = useRef<number | null>(null);
  const drillSeqRef = useRef(0);
  /** The internal drill request in flight; aborted when a newer one
   *  starts, the drill closes or the table unmounts. */
  const drillAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => drillAbortRef.current?.abort(), []);

  const basePageSize = tableConfig.page_size || 10;
  const pageSize = fullscreen ? basePageSize * 4 : basePageSize;

  const rowDrillTarget = tableConfig.row_drill_target ?? null;
  const rowDrillColumn = useMemo(() => {
    if (!rowDrillTarget) return null;
    const explicit = tableConfig.row_drill_param;
    if (explicit) return explicit;
    return tableConfig.columns[0]?.label ?? null;
  }, [rowDrillTarget, tableConfig.row_drill_param, tableConfig.columns]);

  // `row_drill_mode` — swap (default) or accordion. An unknown value is
  // NOT coerced to either: it is kept as an error and this table renders
  // a visible error card instead of guessing (the engine and the builder
  // reject it upstream; this is the last line, for a config written by
  // hand straight into Postgres).
  const drillModeResult = useMemo<
    { mode: RowDrillMode; error: null } | { mode: null; error: string }
  >(() => {
    try {
      return { mode: resolveRowDrillMode(tableConfig.row_drill_mode), error: null };
    } catch (e: unknown) {
      return { mode: null, error: e instanceof Error ? e.message : String(e) };
    }
  }, [tableConfig.row_drill_mode]);
  const drillMode = drillModeResult.mode;

  const filterSig = useMemo(
    () =>
      Object.entries(fetchFilters)
        .filter(([, v]) => v != null && v !== "")
        .map(([k, v]) => `${k}=${v}`)
        .sort()
        .join("&"),
    [fetchFilters],
  );

  // Reset pagination when the filters actually CHANGE — change-only
  // (ref-guarded), because with the lifted view state this component
  // REMOUNTS (band split / breakpoint crossing) and a mount-firing
  // reset would defeat the whole point of the lift (review #2). The
  // search and fullscreen page-resets moved into their handlers for
  // the same reason.
  const filterSigRef = useRef(filterSig);
  useEffect(() => {
    if (filterSigRef.current === filterSig) return;
    filterSigRef.current = filterSig;
    updateView({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSig]);

  // Table identity signature (review #56 finding #10): the builder can
  // RECONFIGURE a table without remounting this component (same grid
  // key, new `tableConfig` object from the layout memo). `hasSettledRef`
  // and the previous rows are stale for the new table identity — without
  // this reset the first load of a reconfigured table shows the OLD rows
  // dimmed under a refresh overlay instead of the loading body. Content
  // signature (not object identity) so unrelated config edits — which
  // rebuild every cfg object — don't reset untouched tables. Cost:
  // JSON.stringify once per config change (memo on the object ref).
  const cfgSig = useMemo(() => JSON.stringify(tableConfig), [tableConfig]);
  const cfgSigRef = useRef(cfgSig);
  useEffect(() => {
    if (cfgSigRef.current === cfgSig) return;
    cfgSigRef.current = cfgSig;
    hasSettledRef.current = false;
    setRows([]);
    setTotal(0);
    setFetchError(null);
  }, [cfgSig]);

  useEffect(() => {
    let cancelled = false;
    // A page nobody will show (new filters / sort / page, unmount) is
    // aborted, and the server stops its query.
    const controller = new AbortController();
    const serverSortBy = sortBy ? (labelToSqlCol.get(sortBy) ?? sortBy) : undefined;
    const args = {
      tableId,
      filters: tableFilters,
      sortBy: serverSortBy,
      sortDir,
      limit: pageSize,
      offset: (page - 1) * pageSize,
      search: search.trim() || undefined,
    };
    // Same request already settled in this dashboard (a remounted
    // table): show it now, refresh silently underneath.
    const cached = data.peekTableData?.(args);
    if (cached) {
      setRows(cached.rows);
      setTotal(cached.total_count);
      setPivotCols(cached.pivot_columns ?? []);
      setFetchError(null);
      hasSettledRef.current = true;
      setLoading(false);
    } else {
      setLoading(true);
    }
    data
      .fetchTableData({ ...args, signal: controller.signal })
      .then((d) => {
        if (cancelled) return;
        setRows(d.rows);
        setTotal(d.total_count);
        setPivotCols(d.pivot_columns ?? []);
        setFetchError(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setFetchError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) {
          hasSettledRef.current = true;
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
    // `cfgSig` (review #56 #10): a reconfigured table (new columns /
    // sort / page size under the same id) refetches immediately — the
    // identity-reset effect above already cleared the stale rows.
    // `retryTick`: the error card / stale badge retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, filterSig, chip, sortBy, sortDir, page, pageSize, search, cfgSig, retryTick]);

  // Shared with the Home board (``table-columns.ts``) so both surfaces
  // filter `hidden`, resolve the `key` fallback and honour per-column
  // render modes identically.
  const columns: ColumnDef[] = useMemo(() => {
    const declared = toColumnDefs(tableConfig.columns as never, crossFilterKeys);
    if (pivotCols.length === 0) return declared;
    // Discovered columns go after the declared one named by
    // `insert_after` (after the last one by default), all with the
    // pivot's render (a state cell for the availability matrix).
    const render = tableConfig.pivot?.render;
    const states = tableConfig.pivot?.states ?? undefined;
    const discovered = pivotCols.map((pc) => ({
      key: pc.key,
      label: pc.label,
      format: "text" as const,
      sortable: true,
      render: render && KNOWN_COLUMN_RENDERS.has(render) ? (render as ColumnRender) : undefined,
      states: states && Object.keys(states).length > 0 ? states : undefined,
      cellStyle: tableConfig.pivot?.cell_style === "fill" ? ("fill" as const) : undefined,
    }));
    const anchor = tableConfig.pivot?.insert_after;
    // A hidden anchor is not in `declared`: fall back to the end.
    const at = anchor ? declared.findIndex((c) => c.key === anchor) : -1;
    if (at === -1) return declared.concat(discovered);
    return [...declared.slice(0, at + 1), ...discovered, ...declared.slice(at + 1)];
  }, [tableConfig.columns, tableConfig.pivot, crossFilterKeys, pivotCols]);

  const handleSort = (col: string): void => {
    if (sortBy === col) {
      updateView({ sortDir: sortDir === "asc" ? "desc" : "asc", page: 1 });
    } else {
      updateView({ sortBy: col, sortDir: "desc", page: 1 });
    }
  };

  useEffect(() => {
    drillReqRef.current = null;
    drillAbortRef.current?.abort();
    setDrillValue(null);
    setDrillRow(null);
    setDrillData(null);
    setDrillError(null);
    setDrillLoading(false);
  }, [filterSig]);

  // Controlled↔uncontrolled boundary (review #6): when `externalDrill`
  // appears or disappears (desktop↔mobile crossing — the container is
  // unkeyed, so the table does NOT remount anymore), the INTERNAL drill
  // state is stale for the new mode. Clear it so e.g. a drill opened on
  // mobile can't reopen from stale internal state after a round-trip
  // through desktop (the host store closes ITS drills on crossing).
  const hadExternalDrillRef = useRef(externalDrill != null);
  useEffect(() => {
    const has = externalDrill != null;
    if (hadExternalDrillRef.current === has) return;
    hadExternalDrillRef.current = has;
    drillReqRef.current = null;
    setDrillValue(null);
    setDrillRow(null);
    setDrillData(null);
    setDrillError(null);
    setDrillLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalDrill != null]);

  const fetchAllRows = useCallback(async (): Promise<Record<string, unknown>[]> => {
    if (!data.fetchAllTableRows) return rows;
    const serverSortBy = sortBy ? (labelToSqlCol.get(sortBy) ?? sortBy) : undefined;
    return data.fetchAllTableRows({
      tableId,
      filters: tableFilters,
      sortBy: serverSortBy,
      sortDir,
      // Analyst feedback 2026-07-15: exporting while a search is active
      // must export the SEARCHED set, not the whole table.
      search: (search ?? "").trim() || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, tableFilters, sortBy, sortDir, search, labelToSqlCol, rows]);

  // QA-F01: when the host can export on the server, "export all" goes
  // there (parts of 250 000 rows; a ZIP for 2+ parts). The browser never
  // holds the full result set.
  const exportAll = useMemo(() => {
    if (!data.exportTable) return undefined;
    const exportTable = data.exportTable.bind(data);
    return (format: TableExportFormat) => {
      const serverSortBy = sortBy ? (labelToSqlCol.get(sortBy) ?? sortBy) : undefined;
      return exportTable({
        tableId,
        filters: tableFilters,
        sortBy: serverSortBy,
        sortDir,
        search: (search ?? "").trim() || undefined,
        format,
        exportName,
      });
    };
  }, [data, tableId, tableFilters, sortBy, sortDir, search, labelToSqlCol, exportName]);

  const closeInternalDrill = (): void => {
    drillReqRef.current = null;
    drillAbortRef.current?.abort();
    setDrillValue(null);
    setDrillRow(null);
    setDrillData(null);
    setDrillError(null);
    setDrillLoading(false);
  };

  const handleRowClick = (rowValue: string, row: Record<string, unknown>): void => {
    if (!rowDrillTarget) return;
    viewChangeByUserRef.current = true;
    // Review #16 (swap only): capture the rows-zone scroll BEFORE the
    // face is hidden (display:none discards the scroll position) so
    // "Back" can restore the user's spot. The accordion never hides the
    // face — the zone keeps scrolling live, so nothing to save.
    if (drillMode === "swap") {
      const zone = tableFaceRef.current?.querySelector<HTMLElement>(
        "[data-rows-zone]",
      );
      if (zone) savedScrollTopRef.current = zone.scrollTop;
    }
    // Controlled (grid) mode — the parent owns fetch + state.
    if (externalDrill) {
      externalDrill.onToggle(rowValue, row);
      return;
    }
    if (drillValue === rowValue) {
      closeInternalDrill();
      return;
    }
    const reqId = ++drillSeqRef.current;
    drillReqRef.current = reqId;
    drillAbortRef.current?.abort();
    const controller = new AbortController();
    drillAbortRef.current = controller;
    setDrillValue(rowValue);
    setDrillRow(row);
    setDrillLoading(true);
    setDrillError(null);
    setDrillData(null);
    data
      .fetchRowDrill({
        tableId,
        drillId: rowDrillTarget,
        rowValue,
        filters: fetchFilters,
        signal: controller.signal,
      })
      .then((d) => {
        // Settled guard (review #2b): apply only when this row is still
        // the CURRENT request — an out-of-order settle from an
        // abandoned request must not overwrite the live drill.
        if (drillReqRef.current === reqId) setDrillData(d);
      })
      .catch((e: unknown) => {
        if (drillReqRef.current === reqId) {
          setDrillError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        if (drillReqRef.current === reqId) setDrillLoading(false);
      });
  };

  // ── Drill = a state of the cell, shown per `row_drill_mode` ──
  // SWAP (default): the face is kept MOUNTED but hidden (display:none +
  // aria-hidden) and the panel renders in the same slot — "Back"
  // restores it instantly, no refetch, no flash, page/sort/search
  // intact. ACCORDION (opt-in): the table face stays as it is;
  // `DataTable` mounts the panel in an expansion row under the active
  // row, inside its scrolling zone. In BOTH the cell's size never
  // changes and the grid is never re-simulated.
  const activeDrillValue = externalDrill ? externalDrill.activeValue : drillValue;
  const drillOpen = rowDrillTarget != null && activeDrillValue != null;
  const swapOpen = drillOpen && drillMode === "swap";
  const accordionEnabled = rowDrillTarget != null && drillMode === "accordion";
  const drillLoadingNow = externalDrill ? externalDrill.loading : drillLoading;

  // Fullscreen × drill (review #1, SWAP only): opening a swap drill
  // EXITS fullscreen — the drill is the new focus of attention, and the
  // fullscreen overlay (`fixed inset-0`) lives inside the hidden table
  // face, so leaving it on would strand `fullscreen=true` + the ×4
  // pageSize as invisible ghosts that resurrect on "Back". The page
  // resets with the pageSize (same rule as the user toggling fullscreen
  // off). The ACCORDION lives inside the table's rows zone, which the
  // fullscreen overlay also hosts — so it simply expands in there and
  // fullscreen stays on.
  useEffect(() => {
    if (swapOpen && fullscreen) {
      setFullscreen(false);
      updateView({ page: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swapOpen]);

  // A11y: when the drill closes, focus returns to the table face —
  // but ONLY for closes the user initiated from the panel (Back /
  // Escape); invalidation closes (filter change, instance swap,
  // breakpoint crossing) must not steal focus (review #4). Layout
  // effect so the scroll restore (review #16) happens before paint.
  const tableFaceRef = useRef<HTMLDivElement>(null);
  /** ¿El ultimo cambio de vista lo pidio el usuario? Solo entonces se
   *  desliza: ver el comentario de `handleRowClick`. */
  const viewChangeByUserRef = useRef(false);
  const wasDrillOpenRef = useRef(drillOpen);
  const closedByUserRef = useRef(false);
  const savedScrollTopRef = useRef(0);
  useLayoutEffect(() => {
    if (wasDrillOpenRef.current && !drillOpen) {
      if (closedByUserRef.current) {
        tableFaceRef.current?.focus({ preventScroll: true });
        // Restore the rows-zone scroll captured at open time —
        // display:none discarded it (review #16). Swap only: the
        // accordion's zone never left the screen, and rewinding it to
        // the open-time position would yank a reader who scrolled the
        // detail meanwhile.
        if (drillMode === "swap") {
          const zone = tableFaceRef.current?.querySelector<HTMLElement>(
            "[data-rows-zone]",
          );
          if (zone) zone.scrollTop = savedScrollTopRef.current;
        }
      }
      closedByUserRef.current = false;
    }
    wasDrillOpenRef.current = drillOpen;
    viewChangeByUserRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillOpen]);

  /** Explicit close from the panel (Back / Escape) — marks the close as
   *  user-initiated so the focus/scroll restore runs (review #4). */
  const handlePanelClose = (): void => {
    closedByUserRef.current = true;
    viewChangeByUserRef.current = true;
    if (externalDrill) externalDrill.onClose();
    else closeInternalDrill();
  };

  // Shared panel wiring — the SAME props feed the accordion (mounted by
  // DataTable under the row) and the swap (mounted here in the cell's
  // slot). Only the chrome variant and the size mode differ. Built on
  // demand: both call sites only render with a drill OPEN, which is
  // what makes `activeDrillValue` non-null there.
  // Tasks: the report flag / mark on each row, when the host provides
  // the adapter AND this table declares which fields identify a row.
  const rowFlags = useRowFlags();
  const identityFields = tableConfig.row_identity ?? [];
  const renderRowMark =
    rowFlags && identityFields.length > 0
      ? (row: Record<string, unknown>) => {
          const identity = rowIdentity(row, identityFields);
          if (identity === null) return null;
          return (
            <RowMark
              adapter={rowFlags}
              anchorRef={{ tableId, drillId: null, rowValue: null, identity }}
              row={row}
              columns={columns}
            />
          );
        }
      : undefined;

  const panelProps = () => ({
    markContext: rowDrillTarget ? { tableId, drillId: rowDrillTarget } : undefined,
    tableTitle: resolveLocalized(tableConfig.title as DisplayLabel, lang) || "",
    rowValue: activeDrillValue!,
    row: externalDrill ? externalDrill.activeRow : drillRow,
    columns: tableConfig.columns,
    rowDrillKey: rowDrillColumn,
    data: externalDrill ? externalDrill.data : drillData,
    loading: drillLoadingNow,
    error: externalDrill ? externalDrill.error : drillError,
    chartConfigs: drillChartConfigs,
    drillLayout,
    colSpan: drillColSpan,
    onClose: handlePanelClose,
    dataSource: data,
    activeFilters,
    nestedTableId: nestedTableId ?? undefined,
    nestedDrillTarget: nestedDrillTarget ?? undefined,
    nestedDrillParam: nestedDrillParam ?? undefined,
  });

  // Unknown `row_drill_mode` (only reachable by a config written past
  // the engine and the builder, e.g. straight into Postgres): a visible
  // error card in the table's slot. Never "treat it as swap".
  if (drillModeResult.error !== null) {
    return (
      <MessageCard
        tone="error"
        fill={fillCell}
        title={resolveLocalized(tableConfig.title as DisplayLabel, lang) || undefined}
      >
        {t("table.rowdrill.invalidMode", {
          value: String(tableConfig.row_drill_mode),
        })}{" "}
        <span className="text-neutral-500">({drillModeResult.error})</span>
      </MessageCard>
    );
  }

  // Fail loud (rule 9): the first fetch failed and there are no rows to
  // keep — an error card in the table's slot, never a "0 rows" table.
  if (fetchError !== null && rows.length === 0) {
    return (
      <BlockErrorCard
        message={fetchError}
        onRetry={() => setRetryTick((n) => n + 1)}
        fill={fillCell}
      />
    );
  }

  return (
    <ViewSlide
      // La direccion ES el mensaje (modo swap): al abrir, la tabla sale
      // por la izquierda y el detalle entra por la derecha; "Volver"
      // corre el mismo gesto invertido. Sin esto el reemplazo pasaba de
      // golpe y nada avisaba que habias cambiado de vista. En acordeon
      // no hay cambio de vista — la tabla se queda — asi que la clave no
      // se mueve y no hay cruce.
      viewKey={swapOpen ? `drill:${String(activeDrillValue)}` : "table"}
      direction={swapOpen ? "forward" : "back"}
      disabled={!viewChangeByUserRef.current}
      className={fillCell ? "h-full" : undefined}
      innerClassName={fillCell ? "h-full" : undefined}
    >
    <div className={fillCell ? "h-full" : undefined}>
      <div
        ref={tableFaceRef}
        tabIndex={-1}
        aria-hidden={swapOpen || undefined}
        className={`${swapOpen ? "hidden " : ""}${
          fillCell ? "flex h-full flex-col gap-3" : "space-y-3"
        } outline-none`}
      >
        {/* SWR (PR-B): while a refetch is in flight the CURRENT rows
            stay mounted and readable under the standard card dim —
            the table never blanks. The overlay only ever shows after
            the first settle (an initial fetch keeps the historical
            "Loading data…" body). `blockPointer` (review #56 #8):
            sort / pagination / search / row-click over STALE rows are
            blocked for the refresh window — interacting with data
            that's about to be replaced produces confusing view state
            (e.g. paging into a row count that no longer exists). */}
        <BlockStale
          error={fetchError}
          onRetry={() => setRetryTick((n) => n + 1)}
          className={fillCell ? "min-h-0 flex-1" : undefined}
        >
        <RefreshOverlay
          refreshing={loading && hasSettledRef.current}
          blockPointer
          className={fillCell ? "h-full" : undefined}
          contentClassName={fillCell ? "h-full" : undefined}
        >
        <DataTable
          className={fillCell ? "h-full" : undefined}
          fillHeight={fillCell}
          capPx={capPx}
          rows={rows}
          loading={loading}
          totalCount={total}
          sortBy={sortBy ?? ""}
          sortDir={sortDir}
          onSort={handleSort}
          page={page}
          pageSize={pageSize}
          onPageChange={(p) => updateView({ page: p })}
          search={search}
          onSearchChange={(v) => updateView({ search: v, page: 1 })}
          quickFilters={
            chipsCfg
              ? {
                  options: chipsCfg.options.map((o) => ({
                    value: o.value,
                    label: resolveLocalized(o.label as DisplayLabel, lang),
                  })),
                  active: chip,
                  onChange: (value: string) => updateView({ chip: value, page: 1 }),
                }
              : undefined
          }
          sortPresets={
            tableConfig.sort_presets && tableConfig.sort_presets.length > 0
              ? {
                  options: tableConfig.sort_presets.map((p) => ({
                    label: resolveLocalized(p.label as DisplayLabel, lang),
                    sortBy: p.sort_by,
                    sortDir: p.sort_dir,
                  })),
                  // No explicit sort yet = the table's default order.
                  activeSortBy: sortBy ?? tableConfig.default_sort ?? null,
                  activeSortDir: sortDir,
                  onSelect: (by: string, dir: "asc" | "desc") =>
                    updateView({ sortBy: by, sortDir: dir, page: 1 }),
                }
              : undefined
          }
          columns={columns}
          title={resolveLocalized(tableConfig.title as DisplayLabel, lang) || ""}
          info={
            tableConfig.info
              ? resolveLocalized(tableConfig.info as DisplayLabel, lang)
              : undefined
          }
          subtitle={
            loading
              ? t("common.loading")
              : t("table.rows", { count: total })
          }
          exportName={exportName}
          fetchAllRows={fetchAllRows}
          exportAll={exportAll}
          onRowClick={rowDrillTarget ? handleRowClick : undefined}
          rowDrillKey={rowDrillColumn ?? undefined}
          activeRowValue={activeDrillValue}
          // Controlled (review #1): fullscreen lives HERE, where the
          // drill open/close is visible, so opening a swap drill can
          // exit it.
          fullscreen={fullscreen}
          onFullscreenChange={(next) => {
            setFullscreen(next);
            // pageSize quadruples/restores — restart pagination (was a
            // mount-firing effect before the view-state lift).
            updateView({ page: 1 });
          }}
          onCellFilter={onCrossFilter}
          activeFilters={activeFilters}
          totalRowKey={tableConfig.total_row_key || undefined}
          highlightRowKey={tableConfig.highlight_row_key || undefined}
          renderRowMark={renderRowMark}
          // Accordion: the panel mounts in the expansion row under the
          // active row, bounded by the rows-zone budget DataTable
          // measured (row + detail always fit together). The reveal
          // key re-runs the scroll-into-view once the detail settles.
          renderRowExpansion={
            accordionEnabled
              ? ({ maxPx }) => (
                  <RowDrillPanel
                    {...panelProps()}
                    variant="accordion"
                    fillParent={false}
                    capPx={maxPx}
                  />
                )
              : undefined
          }
          expansionRevealKey={
            accordionEnabled
              ? `${String(activeDrillValue)}:${drillLoadingNow ? "loading" : "settled"}`
              : undefined
          }
        />
        </RefreshOverlay>
        </BlockStale>
      </div>
      {swapOpen && (
        <RowDrillPanel
          {...panelProps()}
          variant="swap"
          fillParent={fillCell}
          capPx={capPx}
        />
      )}
    </div>
    </ViewSlide>
  );
}

// ---------------------------------------------------------------------------
// GridCell — one memoized grid item (review #9d).
// ---------------------------------------------------------------------------

/** A grid item spec + the fetched data attached by the layout memo. */
type RenderableItem = GridItemSpec & {
  chartData?: ChartData;
  /** Data signature — FadeSwap key for the tier-1 chart crossfade. */
  chartSig?: string;
  textValue?: TextValue;
};

// Hoisted empty defaults — inline `?? []` would mint a new array every
// render (review #9d).

interface GridCellProps {
  sectionId: string;
  item: RenderableItem;
  /** Dibuja el KPI `idx` con la MISMA tarjeta que usa la tira.
   *  `DashboardBody` lo arma porque los valores y `renderCard` viven
   *  ahí; la celda solo lo invoca. Devuelve null si ese KPI todavia no
   *  tiene valor (la tanda no llego). */
  renderKpi?: (idx: number) => JSX.Element | null;
  /** Desktop grid vs mobile stack rendering. */
  desktop: boolean;
  /** Desktop only — placement (row start is segment-relative). */
  colStart?: number;
  colSpan?: number;
  relRowStart?: number;
  rowSpan?: number;
  /** Pixel height of the cell: `cellPx(final rowSpan)` on desktop,
   *  `cellPx(authored heightUnits)` on mobile (cap/floor semantics). */
  px: number;
  activeFilters: Record<string, string>;
  /** See `DashboardBodyProps.queryFilters` (already resolved here). */
  queryFilters: Record<string, string>;
  data: DashboardDataSource;
  instanceKey: string;
  onCrossFilter?: (filterKey: string, rawValue: string) => void;
  crossFilterKeys: ReadonlySet<string>;
  /** In-app jump of a chart's ``row_action`` / ``footer_action`` (the
   *  KPI action path: sibling report + filters, "← Volver a"). ``row`` is the
   *  clicked row: its ``action_value`` goes into ``filter_key`` and the
   *  ``filter_map`` columns into their filters. */
  onChartAction?: (action: ChartAction, row?: Record<string, unknown>) => void;
  /** Saved-views portfolio (DSA): the report's filters (each card prints
   *  its view as chips), the host's "apply this view" + report jump, the
   *  empty state's "save a view", and a revision the host bumps when the
   *  caller's saved filters changed (refetch). All stable refs, so they
   *  ride in `cellCommon` without breaking the memo cascade. */
  filters?: ReadonlyArray<DashboardFilter>;
  onOpenSavedView?: (view: SavedViewRef, target: { report: string | null }) => void;
  onSaveView?: () => void;
  savedViewsRevision?: number;
  /** Dashboard-level tone bands a scorecard block inherits when it does
   *  not declare its own. Stable ref (config.thresholds) so it rides in
   *  `cellCommon` without breaking the memo cascade (regla 9). */
  dashboardThresholds?: ChartConfig["thresholds"];
  /** Drill-swap (ronda 7): the chart whose ``swap_on`` equals THIS
   *  chart's ``cross_filter_key``, plus its positional data. Passed
   *  per-cell at the callsite (NOT via cellCommon — its data identity
   *  changes with every charts settle and would break the memo cascade
   *  for unrelated cells). */
  swapCfg?: ChartConfig;
  swapData?: ChartData;
  /** Open row-drill state for THIS item (desktop, host-controlled). */
  drillState: RowDrillState | null;
  onToggleRowDrill: (
    gridKey: string,
    tableId: string,
    drillId: string,
    rowValue: string,
    row: Record<string, unknown>,
  ) => void;
  onCloseRowDrill: (gridKey: string) => void;
  tableViewState: TableViewState | null;
  onTableViewStateChange: (gridKey: string, next: TableViewState) => void;
  /** SWR (PR-B, memo-safe — review #56 #7) — THIS cell's block state
   *  only, as primitives: a chart cell receives the charts block's
   *  flags, a text cell the texts block's, a table cell idle values
   *  (tables self-manage from their internal fetch). Passing only the
   *  own-block values keeps `React.memo` effective: a texts settle
   *  never re-renders chart cells. */
  refreshing: boolean;
  /** Error of THIS cell's block fetch (null = healthy). */
  blockError: string | null;
  /** Relaunches ONLY this cell's block fetch. Stable per block. */
  onRetryBlock?: () => void;
  /** Inspector (builder-only). */
  inspectorSelected: boolean;
  onSelectItem?: (sectionId: string, itemId: string) => void;
}

/** A chart's ``row_action`` or ``footer_action`` as the jump sees it. */
type ChartAction = {
  label: DisplayLabel;
  report?: string | null;
  href?: string | null;
  filters?: Record<string, string> | null;
  filter_key?: string | null;
  filter_map?: Record<string, string> | null;
};

/** Test-only probe (review #56 #7 memo-cascade pin): counts GridCell
 *  renders per item key so tests can assert that e.g. a texts settle
 *  does NOT re-render chart cells. Disabled by default — production
 *  pays a single boolean check per cell render. */
export const GRID_CELL_RENDER_PROBE: {
  enabled: boolean;
  counts: Map<string, number>;
} = { enabled: false, counts: new Map() };

/** Memoized cell: when a row drill opens or a table pages, only the
 *  affected cell re-renders — sibling charts bail out (specs/data come
 *  from the layout memo, callbacks are useCallback'd, per-item state
 *  arrives as primitives). */
const GridCell = memo(function GridCell({
  sectionId,
  item,
  desktop,
  colStart,
  colSpan,
  relRowStart,
  rowSpan,
  px,
  activeFilters,
  queryFilters,
  data,
  instanceKey,
  onCrossFilter,
  crossFilterKeys,
  onChartAction,
  dashboardThresholds,
  filters,
  onOpenSavedView,
  onSaveView,
  savedViewsRevision,
  drillState,
  onToggleRowDrill,
  onCloseRowDrill,
  tableViewState,
  onTableViewStateChange,
  refreshing,
  blockError,
  onRetryBlock,
  inspectorSelected,
  onSelectItem,
  swapCfg,
  swapData,
  renderKpi,
}: GridCellProps): JSX.Element {
  const lang = useLang();
  const { t } = useTranslation();
  // Tasks: the flag of a chart card and its «pick a point» mode.
  const rowFlags = useRowFlags();
  const [picking, setPicking] = useState(false);

  // Test-only render probe (memo-cascade pin, review #56 #7). Inactive
  // unless a test flips `enabled` — a Map.set per render otherwise
  // never runs.
  if (GRID_CELL_RENDER_PROBE.enabled) {
    GRID_CELL_RENDER_PROBE.counts.set(
      item.key,
      (GRID_CELL_RENDER_PROBE.counts.get(item.key) ?? 0) + 1,
    );
  }

  let inner: ReactNode;
  let hFull: boolean;
  if (item.kind === "chart" && !item.chartData) {
    // Review #56 #2 — the charts block's fetch FAILED and there is no
    // previous payload to keep (first load of this report): the cell
    // renders a compact error card in its exact layout slot, with a
    // retry that relaunches ONLY the charts fetch. (Items without data
    // are only emitted by the layout memo when the block errored.)
    hFull = true;
    inner = (
      <BlockErrorCard
        message={blockError ?? t("dashboard.error.charts")}
        onRetry={onRetryBlock}
        fill
      />
    );
  } else if (item.kind === "chart" && item.chart?.cfg?.type === "ai_readout") {
    // The one block that is NOT SQL. Prose has no fact table, so it
    // does not ride the chart pipeline: it fetches the report's
    // AI-analysis endpoint itself and always renders with the
    // AI marker, so generated narrative is never mistaken for a
    // measured number sitting in the same grid.
    hFull = true;
    inner = (
      <AiReadoutCard
        dataSource={data}
        activeFilters={queryFilters}
        title={
          resolveLocalized(item.chart.cfg.title as DisplayLabel, lang) || undefined
        }
      />
    );
  } else if (item.kind === "chart" && item.chart?.cfg?.type === "saved_views_portfolio") {
    // The other block that is NOT SQL (DSA): the caller's saved filters,
    // each read as the report's own KPIs by the HOST — the engine has no
    // caller. Fetches on its own like the readout; a card click applies
    // the view through the host and jumps to the element's tab.
    hFull = true;
    inner = (
      <SavedViewsPortfolioCard
        dataSource={data}
        cfg={item.chart.cfg}
        filters={filters ?? []}
        title={
          resolveLocalized(item.chart.cfg.title as DisplayLabel, lang) || undefined
        }
        onOpenView={onOpenSavedView}
        onSaveView={onSaveView}
        refreshKey={savedViewsRevision}
      />
    );
  } else if (item.kind === "chart") {
    const cfg = item.chart!.cfg;
    const chart = item.chartData!;
    // Charts fill their cell on BOTH breakpoints (mobile wraps them in a
    // fixed-height div below).
    hFull = true;
    // Drill-swap (ronda 7): while this chart's cross-filter key holds a
    // value AND a sibling chart declared `swap_on` for it, the CELL
    // swaps to that detail chart — the row-drill feel: same cell, same
    // size, a back bar on top, "Back" clears the key and the original
    // content returns. The detail data is positional like every chart.
    const swapKey = cfg.cross_filter_key || "";
    const swapValue = swapKey ? (activeFilters[swapKey] ?? "") : "";
    const swapActive = !!(swapCfg && swapData && swapValue);
    const plot = swapActive ? (
      <div className="flex h-full flex-col gap-2">
        <div className="flex shrink-0 items-center gap-3 rounded-xl border border-primary-line bg-primary-soft px-4 py-2">
          <button
            type="button"
            onClick={() => onCrossFilter?.(swapKey, swapValue)}
            className="inline-flex items-center gap-1.5 rounded-md bg-white px-2 py-1 text-xs font-semibold text-primary-ink ring-1 ring-primary-line transition-colors hover:bg-primary-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <ArrowLeft size={13} /> {t("table.rowdrill.back")}
          </button>
          <span className="truncate text-xs text-neutral-600">
            {resolveLocalized(cfg.title as DisplayLabel, lang)}
            {" › "}
            {swapValue.replace("|", " - ")}
          </span>
        </div>
        <div className="min-h-0 flex-1">
          <GenericChart
            type={swapData!.type}
            title={resolveLocalized(swapCfg!.title as DisplayLabel, lang)}
            info={
              resolveLocalized(swapCfg!.info as DisplayLabel, lang) || undefined
            }
            thresholds={
              swapCfg!.thresholds?.length
                ? swapCfg!.thresholds
                : dashboardThresholds
            }
            subtitle={
              resolveLocalized(swapCfg!.subtitle as DisplayLabel, lang) ||
              undefined
            }
            data={swapData!.data}
            error={swapData!.error}
            cellPx={px - 48}
          />
        </div>
      </div>
    ) : (
      <GenericChart
        // Config→props lives in ONE place (``chart-props.ts``) so the
        // Home board renders this chart identically. It used to be
        // duplicated here and in the board's ChartTile; the copies
        // drifted and the board silently lost meter_max / card_columns
        // / segments / thresholds.
        {...(chartPropsFromConfig(cfg, {
          dashboardThresholds,
          resolveLabel: (label) => resolveLocalized(label, lang),
        }) as unknown as Record<string, never>)}
        type={chart.type}
        title={resolveLocalized(chart.title as DisplayLabel, lang)}
        data={chart.data}
        error={chart.error}
        cellPx={px}
        // Cross-filter wiring for clickable scorecards (kpi_period_tiles):
        // the tile row IS the period selector. Pass the target filter, its
        // current value (highlights the active tile) and the host toggle.
        // Absent cross_filter_key ⇒ read-only. The target is a `tabs`
        // filter — handled directly, NOT via the crossFilterKeys memo
        // (which is table-column scoped and excludes tabs).
        crossFilterKey={cfg.cross_filter_key || undefined}
        activeCrossValue={
          crossFilterActiveValue(activeFilters[cfg.cross_filter_key || ""]) ?? undefined
        }
        // Tasks: in «pick a point» mode the click PICKS (the point is
        // reported) instead of filtering; one pick, then back to normal.
        onCrossFilter={
          picking && rowFlags?.onFlagItem
            ? (key: string, value: string) => {
                setPicking(false);
                rowFlags.onFlagItem!({
                  anchorType: "point",
                  itemLabel: resolveLocalized(cfg.title as DisplayLabel, lang),
                  itemId: item.itemId,
                  shown: { [key]: value },
                });
              }
            : onCrossFilter
        }
        controls={
          cfg.controls && cfg.controls.length > 0 && onCrossFilter
            ? cfg.controls
                // ``visible_when``: a chip that only acts in one mode of a
                // sibling control ("Comparar período" only under "Total")
                // hides in the others instead of doing nothing.
                .filter((ctrl) =>
                  Object.entries(ctrl.visible_when ?? {}).every(([other, value]) => {
                    const sibling = cfg.controls!.find((c) => c.key === other);
                    return (activeFilters[other] || sibling?.default) === value;
                  }),
                )
                .map((ctrl) => {
                const current = activeFilters[ctrl.key];
                const active = current || ctrl.default;
                return {
                  key: ctrl.key,
                  label: resolveLocalized(ctrl.label as DisplayLabel, lang) || undefined,
                  options: ctrl.options.map((o) => ({
                    value: o.value,
                    label: resolveLocalized(o.label as DisplayLabel, lang),
                  })),
                  active,
                  // The host's cross-filter toggles: writing the current
                  // value again clears the key, which the SQL reads as
                  // its default. So the default option clears an explicit
                  // choice, another option sets it, the active one is a
                  // no-op.
                  onSelect: (value: string) => {
                    if (value === active) return;
                    if (value === ctrl.default) {
                      if (current) onCrossFilter(ctrl.key, current);
                      return;
                    }
                    onCrossFilter(ctrl.key, value);
                  },
                };
              })
            : undefined
        }
        rowAction={
          cfg.row_action?.label && onChartAction
            ? {
                label: resolveLocalized(cfg.row_action.label as DisplayLabel, lang),
                needsValue: !!cfg.row_action.filter_key,
                onClick: (row: Record<string, unknown>) => onChartAction(cfg.row_action!, row),
              }
            : undefined
        }
        footerAction={
          cfg.footer_action?.label
            ? {
                label: resolveLocalized(cfg.footer_action.label as DisplayLabel, lang),
                href: cfg.footer_action.href ?? undefined,
                onClick:
                  onChartAction &&
                  (cfg.footer_action.report || cfg.footer_action.filters)
                    ? () => onChartAction(cfg.footer_action!)
                    : undefined,
              }
            : undefined
        }
      />
    );
    // SWR (PR-B): the stale plot stays mounted + dimmed while charts
    // refetch; when the new payload lands the plot CROSSFADES (tier-1
    // universal chart transition, plan §2.4 — keyed by data signature
    // so an unchanged chart doesn't animate). `BlockStale` (review #56
    // #2): a FAILED refresh keeps the previous plot visible but marks
    // it — stale data is never presented as current.
    inner = (
      <BlockStale error={blockError} onRetry={onRetryBlock} className="h-full">
        <RefreshOverlay
          refreshing={refreshing}
          className="h-full"
          contentClassName="h-full"
        >
          <FadeSwap
            swapKey={`${item.chartSig ?? ""}|${
              swapActive ? `${swapValue}:${chartDataSig(swapData!)}` : ""
            }`}
            className="h-full"
            innerClassName="h-full"
            // DECISIÓN 2026-07-23 ("esta es la forma que estaba
            // buscando"): charts hard-swap their data on refetch — no
            // crossfade. The refetch language is: numbers/bars MORPH
            // to the new value, LINES redraw left-to-right
            // (useLineDraw redrawKey), soft dim only on slow fetches.
            disabled
          >
            {plot}
          </FadeSwap>
        </RefreshOverlay>
      </BlockStale>
    );
    if (rowFlags?.onFlagItem && !swapActive) {
      const onFlagItem = rowFlags.onFlagItem;
      const chartLabel = resolveLocalized(cfg.title as DisplayLabel, lang);
      inner = (
        <div className="group relative h-full">
          {inner}
          {picking && <PickPointBanner onCancel={() => setPicking(false)} />}
          <ItemFlag
            onFlagWhole={() =>
              onFlagItem({ anchorType: "chart", itemLabel: chartLabel, itemId: item.itemId })
            }
            onPickPoint={cfg.cross_filter_key ? () => setPicking(true) : undefined}
          />
        </div>
      );
    }
  } else if (item.kind === "kpi") {
    // Misma tarjeta que la tira: `in_grid` cambia DONDE se dibuja, no
    // QUE se dibuja — y tampoco cambia el aviso de error/refresco del
    // bloque KPI (regla 9: dato viejo nunca igual a vigente). `h-full`
    // para que la celda mande el alto, igual que los charts.
    hFull = desktop;
    const card = renderKpi ? renderKpi(item.kpi!.idx) : null;
    const hCls = desktop ? "h-full" : undefined;
    inner =
      card == null ? (
        blockError != null ? (
          <BlockErrorCard message={blockError} onRetry={onRetryBlock} fill={desktop} />
        ) : null
      ) : (
        <BlockStale error={blockError} onRetry={onRetryBlock} className={hCls}>
          <RefreshOverlay refreshing={refreshing} className={hCls} contentClassName={hCls}>
            {card}
          </RefreshOverlay>
        </BlockStale>
      );
  } else if (item.kind === "table") {
    const meta = item.table!;
    hFull = desktop;
    const external: ExternalRowDrill | undefined =
      desktop && meta.drillId != null
        ? {
            activeValue: drillState?.rowValue ?? null,
            activeRow: drillState?.row ?? null,
            data: drillState?.data ?? null,
            loading: drillState?.loading ?? false,
            error: drillState?.error ?? null,
            onToggle: (rowValue: string, row: Record<string, unknown>) =>
              onToggleRowDrill(
                item.key,
                meta.tableId,
                meta.drillId!,
                rowValue,
                row,
              ),
            onClose: () => onCloseRowDrill(item.key),
          }
        : undefined;
    inner = (
      <DashboardTable
        tableConfig={meta.cfg}
        activeFilters={activeFilters}
        queryFilters={queryFilters}
        data={data}
        exportName={`${instanceKey}-${meta.cfg.id}`}
        drillChartConfigs={meta.drillCfg?.charts ?? undefined}
        nestedTableId={meta.drillCfg?.nested_table_id ?? undefined}
        nestedDrillTarget={meta.drillCfg?.nested_drill_target ?? undefined}
        nestedDrillParam={meta.drillCfg?.nested_drill_param ?? undefined}
        onCrossFilter={onCrossFilter}
        crossFilterKeys={crossFilterKeys}
        fillCell={desktop}
        capPx={desktop ? undefined : px}
        externalDrill={external}
        drillColSpan={desktop ? colSpan : undefined}
        drillLayout={meta.drillCfg?.drill_layout ?? undefined}
        viewState={tableViewState}
        onViewStateChange={(next) => onTableViewStateChange(item.key, next)}
      />
    );
  } else if (!item.textValue) {
    // Review #56 #2 — texts block failed with nothing to keep: error
    // card in the text's slot (mirror of the chart case above).
    hFull = desktop;
    inner = (
      <BlockErrorCard
        message={blockError ?? t("dashboard.error.texts")}
        onRetry={onRetryBlock}
        fill={desktop}
      />
    );
  } else {
    const txt = item.textValue;
    hFull = desktop;
    const hCls = desktop ? "h-full" : undefined;
    // overflow-hidden: consistent with the chart/table cards — a fixed
    // grid cell clips instead of bleeding over the neighbor (review #11).
    // SWR (PR-B): dim while texts refetch + crossfade when the value
    // actually changes. `BlockStale`: a failed refresh marks the kept
    // value as not-current (review #56 #2).
    inner = (
      <BlockStale error={blockError} onRetry={onRetryBlock} className={hCls}>
        <RefreshOverlay
          refreshing={refreshing}
          className={hCls}
          contentClassName={hCls}
        >
          <FadeSwap
            swapKey={String(txt.value ?? "")}
            className={hCls}
            innerClassName={hCls}
          >
            <div
              className={`rounded-xl border border-neutral-200 bg-white px-4 py-3 shadow-sm overflow-hidden${
                desktop ? " h-full flex flex-col justify-center" : ""
              }`}
              // Mobile stack: hold the AUTHORED grid height as a floor — the
              // same px the desktop cell would get (review #11).
              style={!desktop ? { minHeight: px } : undefined}
            >
              {/* Both may be bilingual: the label as authored `{en, es}`,
                  the value as an SQL-built JSON string (table-cell contract). */}
              <p className="text-sm text-neutral-800">
                {txt.label ? (
                  <span className="font-semibold text-neutral-500">
                    {resolveLocalized(txt.label, lang)}:{" "}
                  </span>
                ) : null}
                <span className="font-semibold">
                  {txt.value == null
                    ? "—"
                    : typeof txt.value === "string"
                      ? resolveLocalized(txt.value, lang)
                      : txt.value}
                </span>
              </p>
            </div>
          </FadeSwap>
        </RefreshOverlay>
      </BlockStale>
    );
  }

  const wrapped =
    onSelectItem && item.itemId ? (
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          onSelectItem(sectionId, item.itemId!);
        }}
        className={
          "cursor-pointer rounded-xl transition-shadow " +
          (inspectorSelected
            ? "ring-2 ring-primary"
            : "hover:ring-2 hover:ring-primary/30") +
          // Row-spanned items keep the h-full chain intact through the
          // inspector wrapper so the card stretch matches the portal.
          (hFull ? " h-full" : "")
        }
      >
        {inner}
      </div>
    ) : (
      inner
    );

  if (desktop) {
    return (
      <div
        style={{
          gridColumn: `${colStart} / span ${colSpan}`,
          gridRow: `${relRowStart} / span ${rowSpan}`,
          minWidth: 0,
          minHeight: 0,
        }}
      >
        {wrapped}
      </div>
    );
  }
  // Mobile stack: charts keep the authored height as a fixed wrapper;
  // tables/texts size naturally (cap/floor applied inside).
  if (item.kind === "chart") {
    return <div style={{ height: px }}>{wrapped}</div>;
  }
  return <div>{wrapped}</div>;
});

// ---------------------------------------------------------------------------
// DashboardBody
// ---------------------------------------------------------------------------

export interface DashboardBodyProps {
  /** Flattened engine-shape config (sections reconstructed from section_id). */
  config: DashboardRenderConfig;
  /** Fetched KPI data — matched by index to ``config.kpis``. */
  kpis: Kpi[];
  /** Fetched chart data — matched by INDEX to ``config.charts`` (PR-3 §2).
   *  The engine guarantees the alignment: ``fetch_charts`` appends exactly
   *  one entry per config chart (success AND error) iterating the same
   *  list ``_serialize_dashboard`` serializes, and the builder preview's
   *  TS flattener walks sections/items in the same order as the Python
   *  flatten. Index is the identity — titles are display-only. */
  charts: ChartData[];
  /** Fetched text values — matched by index to ``config.texts``. */
  texts?: TextValue[];
  /** The filters as the controls show them (highlights, in-chart
   *  controls, scope strip). */
  activeFilters: Record<string, string>;
  /** The filters the data requests carry (tables, drills, AI
   *  readout). Absent = `activeFilters`. The portal passes a copy
   *  that follows `activeFilters` once the user stops clicking, so a
   *  burst of filter changes is ONE round of requests while every
   *  control still answers at once. */
  queryFilters?: Record<string, string>;
  data: DashboardDataSource;
  /** Stable identity for export filenames + drill-cache reset on change. */
  instanceKey: string;
  /** Cross-filter: when set, cells in table columns that declare an
   * eligible ``filter_key`` become clickable and fire this callback
   * with (filterKey, rawValue). The host owns the toggle semantics
   * (set the filter / clear it when the value is already active).
   * Undefined (default) = feature off, tables render exactly as
   * before. Scope v1: main-dashboard tables only (not drill panels). */
  onCrossFilter?: (filterKey: string, rawValue: string) => void;

  /** SWR (PR-B, plan §2.2) — per-block refresh flags from the host's
   *  independent kpis/charts/texts fetches. While a block's flag is
   *  true its content stays MOUNTED and dims under a `RefreshOverlay`;
   *  when the block's data lands it transitions (NumberTween /
   *  FadeSwap). Tables are NOT listed: they fetch through the injected
   *  data source and manage their own refresh dim internally.
   *  Undefined (default) = no overlays, render exactly as before. */
  refreshState?: DashboardRefreshState;

  /* Inspector (builder Dashboard-tab editor only; undefined in portal). */
  onSelectItem?: (sectionId: string, itemId: string) => void;
  onSelectSection?: (sectionId: string) => void;
  selectedItemId?: string | null;

  /** The host draws the Glosario button itself (the portal puts it at
   *  the end of the tabs row, encabezado compacto H1c); the body then
   *  leaves it out instead of drawing a row of its own. */
  hideGlossary?: boolean;

  /** A KPI action with ``filters`` asks the host to write them (an
   *  empty value clears that filter). Undefined = the filters part of
   *  the action is ignored. */
  onApplyFilters?: (filters: Record<string, string>) => void;
  /** Report groups (D8): an action with ``report`` asks the host to open
   *  that SIBLING report of the group, writing the action's ``filters``
   *  on the way (the host owns the group's filters and the tab bar, and
   *  offers "← Volver a"). Undefined = such an action does nothing. */
  onOpenReport?: (reportKey: string, filters: Record<string, string> | null) => void;
  /** Saved-views portfolio (DSA): the host applies the clicked saved
   *  filter — reset to the defaults, the view's dict on top, its SKU
   *  reference, exactly as the saved-filters drawer does — and the body
   *  jumps to the element's tab. ``onSaveView`` opens the host's drawer
   *  from the empty state. ``savedViewsRevision`` changes when the
   *  caller's saved filters changed, so the element refetches. */
  onApplySavedFilter?: (view: SavedViewRef) => void;
  onSaveView?: () => void;
  savedViewsRevision?: number;
  /** Phase 3 (correo v2) — render ONLY these authored item ids: the
   *  scheduled-email capture of a selection. Every other KPI, chart,
   *  table and text is dropped BEFORE the grid simulator, so the grid
   *  re-packs without holes; sections left empty disappear; the tab bar
   *  is not drawn and the picked elements of every tab stack into one
   *  page. `undefined` / `null` = the whole dashboard, exactly as
   *  before. An element without an authored id can never match. */
  visibleItemIds?: ReadonlyArray<string> | null;
  /** Tasks: the report flag on rows of tables that declare
   *  ``row_identity`` (and on row-drill tables). The portal passes it;
   *  the builder Preview omits it and no flag renders. */
  rowFlags?: RowFlagAdapter | null;
}

/** Keep the data entries whose config entry (same index) survives the
 *  pick — the kpis / charts / texts arrays are matched to the config by
 *  INDEX, so filtering one side alone would pair data with the wrong
 *  element. */
function pickByIndex<T>(
  data: readonly T[],
  cfgs: ReadonlyArray<{ item_id?: string }> | undefined,
  keep: (cfg: { item_id?: string } | undefined) => boolean,
): T[] {
  return data.filter((_, i) => keep(cfgs?.[i]));
}

/** One block's refresh + error state (review #56 #2). `error` non-null
 *  means the block's LAST fetch failed: with no data on screen the
 *  block renders an in-slot error card; with stale data on screen the
 *  content stays visible but MARKED (ring + badge) — never
 *  indistinguishable from current data. `onRetry` relaunches only that
 *  block's fetch. */
export interface BlockRefreshState {
  refreshing?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

/** Per-block refresh flags (SWR, PR-B). Each block is an independent
 *  fetch on the host side — a block transitions when ITS data lands,
 *  never waiting for the slowest sibling (plan §2.2 risk note). Plain
 *  booleans remain accepted (refresh-only, no error surfacing) for
 *  hosts that don't track per-block errors. */
export interface DashboardRefreshState {
  kpis?: boolean | BlockRefreshState;
  charts?: boolean | BlockRefreshState;
  texts?: boolean | BlockRefreshState;
}

/** Normalized per-block state used internally (always-present fields). */
type NormalizedBlockState = {
  refreshing: boolean;
  error: string | null;
  onRetry?: () => void;
};

const IDLE_BLOCK: NormalizedBlockState = { refreshing: false, error: null };

function normalizeBlock(
  b: boolean | BlockRefreshState | undefined,
): NormalizedBlockState {
  if (b == null || b === false) return IDLE_BLOCK;
  if (b === true) return { refreshing: true, error: null };
  return { refreshing: !!b.refreshing, error: b.error ?? null, onRetry: b.onRetry };
}

/** One open row-drill (per table grid item, keyed by the item key). */
type RowDrillState = {
  rowValue: string;
  /** Request sequence id — the settled-response guard (audit #195). */
  reqId: number;
  /** The full clicked row — feeds the panel's context chips. */
  row: Record<string, unknown>;
  data: RowDrillData | null;
  loading: boolean;
  error: string | null;
};

/** Per-section computed layout: data-attached items + placements, or a
 *  fail-loud `error` (invalid layout values that bypassed publish
 *  validation) rendered as a visible error card. */
type SectionLayout = {
  bucket: SectionBucketSpec;
  error: string | null;
  /** Authoring order (the mobile stack renders this order). */
  items: RenderableItem[];
  byKey: Map<string, Placement>;
  placements: Placement[];
};

export function DashboardBody({
  config: rawConfig,
  kpis: rawKpis,
  charts: rawCharts,
  texts: rawTexts,
  activeFilters,
  queryFilters: hostQueryFilters,
  data: hostData,
  instanceKey,
  onCrossFilter,
  refreshState,
  onSelectItem,
  onSelectSection,
  selectedItemId,
  hideGlossary,
  onApplyFilters,
  onOpenReport,
  onApplySavedFilter,
  onSaveView,
  savedViewsRevision,
  visibleItemIds,
  rowFlags = null,
}: DashboardBodyProps): JSX.Element {
  // Table pages settled in this instance (`table-data-cache.ts`): a table
  // that remounts repaints from them. A new config starts an empty cache:
  // the same request against a reconfigured table is a different answer
  // (review #56 #10).
  const data = useMemo(() => withTableDataCache(hostData), [hostData, rawConfig]);
  const queryFilters = hostQueryFilters ?? activeFilters;
  // ── Phase 3: capture of a selection ───────────────────────
  // The pick is applied to the CONFIG (so the grid simulator never sees
  // the dropped elements) and, by index, to the data arrays. The legacy
  // singular table has no authored id and cannot be picked.
  const picked = useMemo(
    () => (visibleItemIds ? new Set(visibleItemIds) : null),
    [visibleItemIds],
  );
  const keepItem = useCallback(
    (cfg: { item_id?: string } | undefined): boolean =>
      picked === null ||
      (cfg?.item_id != null && cfg.item_id !== "" && picked.has(cfg.item_id)),
    [picked],
  );
  const config = useMemo<DashboardRenderConfig>(() => {
    if (picked === null) return rawConfig;
    return {
      ...rawConfig,
      kpis: (rawConfig.kpis ?? []).filter(keepItem),
      charts: (rawConfig.charts ?? []).filter(keepItem),
      tables: (rawConfig.tables ?? []).filter(keepItem),
      texts: (rawConfig.texts ?? []).filter(keepItem),
      table: undefined,
    };
  }, [rawConfig, picked, keepItem]);
  const kpis = useMemo(
    () => (picked === null ? rawKpis : pickByIndex(rawKpis, rawConfig.kpis, keepItem)),
    [rawKpis, rawConfig.kpis, picked, keepItem],
  );
  const charts = useMemo(
    () =>
      picked === null ? rawCharts : pickByIndex(rawCharts, rawConfig.charts, keepItem),
    [rawCharts, rawConfig.charts, picked, keepItem],
  );
  const texts = useMemo(
    () =>
      picked === null || rawTexts === undefined
        ? rawTexts
        : pickByIndex(rawTexts, rawConfig.texts, keepItem),
    [rawTexts, rawConfig.texts, picked, keepItem],
  );
  const lang = useLang();
  // Read at catch time through a ref, so the drill effect keeps its deps.
  const { t: tBody } = useTranslation();
  const tBodyRef = useRef(tBody);
  tBodyRef.current = tBody;

  // Pending anchor scroll after a jump (`href: "#sec_x"`).
  const [pendingAnchor, setPendingAnchor] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingAnchor) return;
    const el = document.getElementById(pendingAnchor);
    // jsdom (tests) has no scrollIntoView; guard rather than crash.
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setPendingAnchor(null);
  }, [pendingAnchor]);
  const jumpFromAction = useCallback(
    (action: NonNullable<Kpi["action"]>) => {
      if (action.report) {
        // A sibling report of the group: the host writes the filters and
        // switches the report (its tab bar, its "← Volver a").
        onOpenReport?.(action.report, action.filters ?? null);
        return;
      }
      if (action.filters && onApplyFilters) onApplyFilters(action.filters);
      if (action.href && action.href.startsWith("#")) {
        setPendingAnchor(action.href.slice(1));
      }
    },
    [onApplyFilters, onOpenReport],
  );
  // Chart actions ride the SAME jump. A row action writes the clicked
  // row's value into its ``filter_key``; a multi_select / chip target
  // takes a one-element JSON list, like a cross-filter click.
  // It travels in `cellCommon`, so its identity must be STABLE:
  // `jumpFromAction` changes with its host callbacks, which re-rendered
  // every chart cell on each settle. The ref reads the latest jump and
  // filters at click time.
  const chartActionDeps = useRef({ jump: jumpFromAction, filters: config.filters });
  chartActionDeps.current = { jump: jumpFromAction, filters: config.filters };
  const runChartAction = useCallback((action: ChartAction, row?: Record<string, unknown>) => {
    const { jump, filters: declared } = chartActionDeps.current;
    const filters: Record<string, string> = { ...(action.filters ?? {}) };
    const write = (key: string, raw: unknown) => {
      if (raw == null || raw === "") return;
      const value = String(raw);
      const target = (declared ?? []).find((f) => f.key === key);
      filters[key] =
        target?.type === "multi_select" || target?.type === "chip"
          ? JSON.stringify([value])
          : value;
    };
    if (row) {
      if (action.filter_key) write(action.filter_key, row.action_value);
      for (const [key, column] of Object.entries(action.filter_map ?? {})) {
        write(key, row[column]);
      }
    }
    jump({
      label: action.label,
      report: action.report ?? null,
      href: action.href ?? null,
      filters: Object.keys(filters).length > 0 ? filters : null,
    });
  }, []);
  // Saved-views portfolio (DSA): applying a view is the HOST's write (it
  // resets to the defaults, lays the view's dict on top and keeps the
  // SKU reference, exactly like its drawer); the body only owns the jump
  // to the sibling report — the KPI action's jump, so "← Volver a" works.
  // Stable like `runChartAction` (memo cascade, regla 9): the moving
  // parts live in a ref, the callback never changes identity.
  const savedViewDeps = useRef({ apply: onApplySavedFilter, jump: jumpFromAction });
  savedViewDeps.current = { apply: onApplySavedFilter, jump: jumpFromAction };
  const openSavedView = useCallback(
    (view: SavedViewRef, target: { report: string | null }) => {
      const { apply, jump } = savedViewDeps.current;
      apply?.(view);
      if (target.report) {
        jump({ label: "", report: target.report, href: null, filters: null });
      }
    },
    [],
  );

  // SWR per-block states, normalized once. Downstream everything is
  // primitives + the host's stable onRetry callbacks — memo-safe
  // (review #56 #7).
  const kpisBlock = normalizeBlock(refreshState?.kpis);
  const chartsBlock = normalizeBlock(refreshState?.charts);
  const textsBlock = normalizeBlock(refreshState?.texts);
  const chartsErrored = chartsBlock.error != null;
  const textsErrored = textsBlock.error != null;

  // Cross-filter eligibility — a column's ``filter_key`` only activates
  // when it names a select/autocomplete (or type-less, which defaults to
  // select) filter that is not locked. Computed once per config.
  const crossFilterKeys = useMemo<ReadonlySet<string>>(
    () =>
      new Set(
        (config.filters ?? [])
          .filter(
            (f) =>
              (f.type == null ||
                f.type === "select" ||
                f.type === "autocomplete" ||
                // PR-7: a multi_select target takes a one-element
                // selection (see `crossFilterWrite`).
                f.type === "multi_select") &&
              !f.locked,
          )
          .map((f) => f.key),
      ),
    [config.filters],
  );

  // ── Drill state machine (lifted from GenericDashboardPage) ──
  const [activeDrillId, setActiveDrillId] = useState<string | null>(null);
  const [drillCache, setDrillCache] = useState<Record<string, DrillData>>({});
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState<string | null>(null);

  const filterSig = useMemo(
    () =>
      Object.entries(queryFilters)
        .filter(([, v]) => v != null && v !== "")
        .map(([k, v]) => `${k}=${v}`)
        .sort()
        .join("&"),
    [queryFilters],
  );

  // Invalidate drill cache + close when filters change OR the instance
  // swaps. Guarded (review #9e): keep the same reference when already
  // empty so the mount-firing pass doesn't schedule a no-op re-render.
  useEffect(() => {
    setDrillCache((prev) => (Object.keys(prev).length === 0 ? prev : {}));
    setActiveDrillId(null);
    setDrillError(null);
  }, [filterSig, instanceKey]);

  // Fetch the active drill on demand (cached per drill_id × filters).
  useEffect(() => {
    if (!activeDrillId) return;
    if (drillCache[activeDrillId]) {
      setDrillLoading(false);
      setDrillError(null);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setDrillLoading(true);
    setDrillError(null);
    data
      .fetchDrill({ drillId: activeDrillId, filters: queryFilters, signal: controller.signal })
      .then((payload) => {
        if (cancelled) return;
        setDrillCache((prev) => ({ ...prev, [activeDrillId]: payload }));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setDrillError(err instanceof Error ? err.message : tBodyRef.current("dashboard.error.drill"));
      })
      .finally(() => {
        if (!cancelled) setDrillLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDrillId, filterSig, drillCache]);

  const toggleDrill = useCallback((drillId: string) => {
    setActiveDrillId((prev) => (prev === drillId ? null : drillId));
  }, []);
  const closeDrill = useCallback(() => setActiveDrillId(null), []);

  // ── Row-drill state (uniform grid, desktop) ──
  // Lifted OUT of DashboardTable so the drill survives the memoized
  // GridCell rendering and multiple tables may hold an open drill
  // simultaneously. The drill renders as an IN-PLACE SWAP inside the
  // table's own cell (`RowDrillPanel`) — the grid is never re-simulated
  // and the cell never changes size. Mobile + the legacy singular table
  // keep their own internal state inside DashboardTable (uncontrolled),
  // with the SAME swap render.
  const [rowDrills, setRowDrills] = useState<Record<string, RowDrillState>>({});
  const isDesktop = useIsDesktop();

  // Ref mirrors so the STABLE toggle callback (review #9b) reads the
  // CURRENT values. Deliberately NOT the reviewer-suggested
  // functional-update decision: setState updaters run later (during the
  // re-render) and must stay side-effect-free (StrictMode double-invokes
  // them), so the open/close decision — which gates a fetch — cannot
  // live inside one. A render-time ref mirror gives the same
  // always-current guarantee with a stable callback identity.
  const rowDrillsRef = useRef(rowDrills);
  rowDrillsRef.current = rowDrills;
  const rowDrillSeqRef = useRef(0);
  const queryFiltersRef = useRef(queryFilters);
  queryFiltersRef.current = queryFilters;
  /** Row-drill request in flight per grid cell, aborted when that cell
   *  asks for another row, its drill closes, or the filters change. */
  const rowDrillAbortRef = useRef<Map<string, AbortController>>(new Map());
  const abortRowDrills = useCallback(() => {
    for (const controller of rowDrillAbortRef.current.values()) controller.abort();
    rowDrillAbortRef.current.clear();
  }, []);
  useEffect(() => abortRowDrills, [abortRowDrills]);

  // Close every open drill when filters change or the report instance
  // swaps (mirror of the KPI-drill invalidation above; the drill context
  // is stale). Guarded (review #9e).
  useEffect(() => {
    abortRowDrills();
    setRowDrills((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, [filterSig, instanceKey, abortRowDrills]);

  // Breakpoint crossing: the grid container and the mobile stack are
  // both UNKEYED divs and the cells share their `it.key`s, so React
  // PRESERVES the item instances across the flip — no remount, no
  // table refetch (verified by test; the old "remount total" premise
  // died with the band re-parenting). The lifted drill store is still
  // desktop-only UI state: close open drills on crossing so a stale
  // entry doesn't reopen a swap when the user returns to desktop
  // (DashboardTable clears its INTERNAL drill on the controlled↔
  // uncontrolled boundary for the mobile-opened mirror case). Table
  // view state stays lifted for the report-swap reset (review #2).
  useEffect(() => {
    abortRowDrills();
    setRowDrills((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, [isDesktop, abortRowDrills]);

  const closeRowDrill = useCallback((gridKey: string) => {
    rowDrillAbortRef.current.get(gridKey)?.abort();
    rowDrillAbortRef.current.delete(gridKey);
    setRowDrills((prev) => {
      if (!(gridKey in prev)) return prev;
      const next = { ...prev };
      delete next[gridKey];
      return next;
    });
  }, []);

  // Stable across renders (review #9b) — GridCell is memoized and this
  // callback is one of its props. Settled-response guards drop stale
  // fetches when the user re-clicks before a request lands — keyed by a
  // per-request id, not the row value: the same row reopened under new
  // filters must not take the old-filter response (audit #195).
  const toggleRowDrill = useCallback(
    (
      gridKey: string,
      tableId: string,
      drillId: string,
      rowValue: string,
      row: Record<string, unknown>,
    ): void => {
      const current = rowDrillsRef.current[gridKey];
      if (current && current.rowValue === rowValue) {
        closeRowDrill(gridKey);
        return;
      }
      const reqId = ++rowDrillSeqRef.current;
      rowDrillAbortRef.current.get(gridKey)?.abort();
      const controller = new AbortController();
      rowDrillAbortRef.current.set(gridKey, controller);
      setRowDrills((prev) => ({
        ...prev,
        [gridKey]: { rowValue, reqId, row, data: null, loading: true, error: null },
      }));
      data
        .fetchRowDrill({
          tableId,
          drillId,
          rowValue,
          filters: queryFiltersRef.current,
          signal: controller.signal,
        })
        .then((payload) => {
          setRowDrills((prev) =>
            prev[gridKey]?.reqId === reqId
              ? {
                  ...prev,
                  [gridKey]: {
                    rowValue,
                    reqId,
                    row,
                    data: payload,
                    loading: false,
                    error: null,
                  },
                }
              : prev,
          );
        })
        .catch((e: unknown) => {
          setRowDrills((prev) =>
            prev[gridKey]?.reqId === reqId
              ? {
                  ...prev,
                  [gridKey]: {
                    rowValue,
                    reqId,
                    row,
                    data: null,
                    loading: false,
                    error: e instanceof Error ? e.message : String(e),
                  },
                }
              : prev,
          );
        });
    },
    [closeRowDrill, data],
  );

  // ── Lifted table view state (review #2) ──
  // Keyed by grid-item key; passed as controlled props so pagination /
  // sort / search survive the breakpoint crossing (the swap keeps the
  // table mounted, so the drill itself no longer causes remounts).
  // Reset on report swap; filter changes only reset the page (inside
  // DashboardTable, change-only).
  const [tableStates, setTableStates] = useState<Record<string, TableViewState>>({});
  const setTableState = useCallback((gridKey: string, next: TableViewState) => {
    setTableStates((prev) => ({ ...prev, [gridKey]: next }));
  }, []);
  useEffect(() => {
    setTableStates((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, [instanceKey]);

  // ── Shared bucketing + memoized per-section layouts (#7a/#9a) ──
  const { sections, legacyTable } = useMemo(
    () => bucketDashboardConfig(config),
    [config],
  );


  // Render-item cache (review #56 #7): reuse the SAME RenderableItem
  // object while an item's spec + data references are unchanged, so a
  // layouts recompute triggered by ANOTHER block (e.g. texts landing)
  // hands the memoized GridCell identical props and chart cells bail
  // out — and `chartDataSig` is only recomputed when that chart's
  // payload reference actually changed. Keyed by grid-item key; stale
  // entries are simply overwritten (bounded by the config's item
  // count). Render-phase mutation of a ref is safe here: the compute
  // is deterministic per input set (StrictMode double-invoke lands on
  // the cached branch the second time).
  const itemCacheRef = useRef(
    new Map<
      string,
      {
        spec: GridItemSpec;
        chartData?: ChartData;
        textValue?: TextValue;
        item: RenderableItem;
      }
    >(),
  );

  // Drill-swap (ronda 7): charts declaring ``swap_on`` render inside
  // their target's cell (the chart whose cross_filter_key matches).
  // Keyed by filter key; data stays positional. NOT part of cellCommon —
  // its data ref changes every charts settle, so it's passed per-cell
  // only to the target chart cells (memo cascade intact for the rest).
  const swapEntries = useMemo(() => {
    const m = new Map<string, { cfg: ChartConfig; idx: number }>();
    (config.charts ?? []).forEach((c, i) => {
      if (c.swap_on) m.set(c.swap_on, { cfg: c, idx: i });
    });
    return m;
  }, [config]);

  const layouts = useMemo<SectionLayout[]>(() => {
    const cache = itemCacheRef.current;
    const cachedItem = (
      spec: GridItemSpec,
      chartData: ChartData | undefined,
      textValue: TextValue | undefined,
    ): RenderableItem => {
      const hit = cache.get(spec.key);
      if (
        hit &&
        hit.spec === spec &&
        hit.chartData === chartData &&
        hit.textValue === textValue
      ) {
        return hit.item;
      }
      const item: RenderableItem = {
        ...spec,
        ...(chartData !== undefined
          ? { chartData, chartSig: chartDataSig(chartData) }
          : {}),
        ...(textValue !== undefined ? { textValue } : {}),
      };
      cache.set(spec.key, { spec, chartData, textValue, item });
      return item;
    };
    // Chart data pairing is POSITIONAL (PR-3 §2): `charts[i]` belongs to
    // `config.charts[i]`. Verified order guarantee on both paths:
    //   * portal — `fetch_charts` iterates `report.dashboard.charts` and
    //     appends EXACTLY one entry per config chart (error entries
    //     included), and `_serialize_dashboard` serializes the SAME list,
    //     so config and data are index-aligned by construction;
    //   * builder preview — the TS `flattenForRender` and the Python
    //     `config_compat.flatten_for_engine` walk sections→items in the
    //     same order, so the preview config's chart index equals the
    //     engine's flattened index.
    // The old title-based matching lost the authored layout whenever a
    // title differed between config and data (the chart fell back to
    // width 4 / default height and shifted everything after it).
    return sections.map((bucket): SectionLayout => {
      if (bucket.error) {
        return { bucket, error: bucket.error, items: [], byKey: new Map(), placements: [] };
      }
      const items: RenderableItem[] = [];
      for (const spec of bucket.grid) {
        if (spec.kind === "chart") {
          const chartData = charts[spec.chart!.idx];
          // Data not fetched (yet) — matches the historical data-driven
          // render: the item appears when its data lands. (Transient by
          // design: the fetch returns the full aligned list at once.)
          // EXCEPT when the charts block ERRORED with nothing fetched
          // (review #56 #2): the item is kept so its cell renders an
          // in-slot error card instead of silently vanishing from the
          // layout.
          if (!chartData && !chartsErrored) continue;
          items.push(cachedItem(spec, chartData, undefined));
        } else if (spec.kind === "text") {
          const textValue = texts?.[spec.text!.idx];
          if (!textValue && !textsErrored) continue;
          items.push(cachedItem(spec, undefined, textValue));
        } else {
          items.push(spec);
        }
      }
      try {
        const placements = stretchToFill(
          placeItems(
            items.map((it) => ({ key: it.key, width: it.width, height: it.heightUnits })),
          ),
        );
        const byKey = new Map<string, Placement>(placements.map((p) => [p.key, p]));
        return { bucket, error: null, items, byKey, placements };
      } catch (e) {
        // Fail loud, scoped: this section renders a visible error card
        // (review #3/#5) — never a silent clamp, never a renderer freeze.
        return {
          bucket,
          error: e instanceof Error ? e.message : String(e),
          items: [],
          byKey: new Map(),
          placements: [],
        };
      }
    });
  }, [sections, charts, texts, chartsErrored, textsErrored]);

  // Inspector wrapper for KPI cards (grid items use GridCell's own).
  const inspectorOn = !!onSelectItem;
  const wrapSelectable = (
    sectionId: string,
    itemId: string | undefined,
    child: ReactNode,
  ): ReactNode => {
    if (!inspectorOn || !itemId) return child;
    const isSelected = selectedItemId === itemId;
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          onSelectItem?.(sectionId, itemId);
        }}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          e.stopPropagation();
          onSelectItem?.(sectionId, itemId);
        }}
        className={
          "cursor-pointer rounded-xl transition-shadow " +
          (isSelected ? "ring-2 ring-primary" : "hover:ring-2 hover:ring-primary/30")
        }
      >
        {child}
      </div>
    );
  };

  // `in_grid` KPIs (#459) live in `b.grid`, not in `b.kpis` — a drill they
  // open belongs to their section just like a strip KPI's.
  const sectionOwnsActiveDrill = (b: SectionBucketSpec): boolean =>
    !!activeDrillId &&
    (b.kpis.some((entry) => entry.cfg.drill_target === activeDrillId) ||
      b.grid.some(
        (it) => it.kind === "kpi" && it.kpi?.cfg.drill_target === activeDrillId,
      ));

  const renderCard = (kpi: Kpi, cfg: KpiConfig, i: number): JSX.Element => {
    const drillTarget = cfg.drill_target ?? null;
    const variant = (kpi.variant ?? "card") as string;
    // SWR (PR-B): numeric KPI values on display-only variants TWEEN to
    // the new value (NumberTween), formatting every frame through the
    // SAME `formatCellValue` the static path uses — the landed value
    // renders byte-identical to before. KPI values whose raw payload is
    // a pre-formatted STRING (and the numeric variants that parse the
    // prop, see KPI_TWEEN_VARIANTS) keep the plain string — never
    // animated, never reformatted.
    const rawValue = kpi.value;
    // Ronda 14 (decision B): STRING values on display variants get the
    // "try" — if the pre-formatted string parses unambiguously to a
    // number (`parseFormattedNumber`), count up from 0 IN THE ORIGINAL
    // FORMAT; the final frame renders the original string
    // byte-identical (`parsed.render(value)` returns it literally).
    // If it does not parse, a subtle fade+rise (`FadeRiseIn`) — the
    // string renders as-is, only its appearance animates.
    const value: string | JSX.Element = (() => {
      if (!KPI_TWEEN_VARIANTS.has(variant)) {
        return formatCellValue(rawValue, kpi.format);
      }
      if (typeof rawValue === "number" && Number.isFinite(rawValue)) {
        return (
          <NumberTween
            fromZero
            durationMs={MOTION.durEntrance}
            delayMs={MOTION.delayEntrance}
            value={rawValue}
            format={(n) => formatCellValue(n, kpi.format)}
          />
        );
      }
      // Parse the DISPLAY string (post-formatCellValue) so the final
      // frame is byte-identical to exactly what the static path
      // would have rendered.
      const display = formatCellValue(rawValue, kpi.format);
      if (typeof rawValue === "string" && typeof display === "string") {
        const parsed = parseFormattedNumber(display);
        if (parsed) {
          return (
            <NumberTween
              fromZero
              durationMs={MOTION.durEntrance}
              delayMs={MOTION.delayEntrance}
              value={parsed.value}
              format={parsed.render}
            />
          );
        }
        return <FadeRiseIn>{display}</FadeRiseIn>;
      }
      return display;
    })();
    const card = (
      <KPICard
        title={resolveLocalized(kpi.label as DisplayLabel, lang)}
        value={value}
        accentColor={KPI_ACCENTS[i % KPI_ACCENTS.length] as never}
        variant={(kpi.variant ?? "card") as never}
        max={kpi.max ?? undefined}
        gradient={kpi.gradient ?? undefined}
        icon={kpi.icon ?? undefined}
        iconBg={kpi.icon_bg ?? undefined}
        sparkData={kpi.spark_data ?? undefined}
        previous={
          kpi.previous == null
            ? undefined
            : kpi.format === "percent"
              ? kpi.previous * 100
              : kpi.previous
        }
        numericValue={
          typeof kpi.value === "number"
            ? kpi.format === "percent"
              ? kpi.value * 100
              : kpi.value
            : undefined
        }
        format={(kpi.format ?? undefined) as never}
        unit={kpi.unit ? resolveLocalized(kpi.unit as DisplayLabel, lang) : undefined}
        severity={kpi.severity ?? undefined}
        description={
          kpi.description ? resolveLocalized(kpi.description as DisplayLabel, lang) : undefined
        }
        expected={kpi.expected ?? undefined}
        breakdown={kpi.breakdown ?? undefined}
        drillTargetId={drillTarget ?? undefined}
        isActive={drillTarget !== null && drillTarget === activeDrillId}
        // DSA strip (`core`): one focus at a time — while another KPI's
        // analysis is open the CORE badge steps back.
        badge={
          kpi.badge && (!activeDrillId || activeDrillId === drillTarget)
            ? resolveLocalized(kpi.badge as DisplayLabel, lang)
            : undefined
        }
        // The highlighted metric keeps its ring until another KPI's
        // analysis opens (one focus at a time, same rule as the badge).
        focused={
          kpi.focused ? !activeDrillId || activeDrillId === drillTarget : undefined
        }
        joiner={kpi.joiner ?? undefined}
        goalDirection={cfg.goal_direction ?? undefined}
        onDrillClick={drillTarget ? toggleDrill : undefined}
        info={
          kpi.info ? resolveLocalized(kpi.info as DisplayLabel, lang) : undefined
        }
        // Epic v3 (scorecards): the bands come from the KPI payload,
        // never from the rendered string — the tint must follow the
        // NUMBER, and `value` here may already be a formatted node.
        valueTone={
          kpi.value_thresholds && kpi.value_thresholds.length > 0
            ? { thresholds: kpi.value_thresholds }
            : undefined
        }
        valueToneValue={typeof kpi.value === "number" ? kpi.value : undefined}
        caption={
          kpi.caption?.text
            ? {
                text: resolveLocalized(kpi.caption.text as DisplayLabel, lang),
                tone: kpi.caption.tone,
              }
            : undefined
        }
        // Franja de accion del pie de la tarjeta. `href` viaja tal cual:
        // sin el, la banda se rendea DESHABILITADA en vez de como boton.
        action={
          kpi.action?.label
            ? {
                label: resolveLocalized(kpi.action.label as DisplayLabel, lang),
                href: kpi.action.href ?? undefined,
                note: kpi.action.note
                  ? resolveLocalized(kpi.action.note as DisplayLabel, lang)
                  : undefined,
                // In-app jump: a sibling report and/or filters make the
                // band a live button; the host receives both.
                onClick:
                  kpi.action.report || kpi.action.filters
                    ? () => jumpFromAction(kpi.action!)
                    : undefined,
              }
            : undefined
        }
      />
    );
    // Compare KPIs: the value AND the delta chip live inside the
    // bi-components `KPICompare` (below this package in the dep graph,
    // so the chip can't be wrapped individually) — crossfade the whole
    // card when current/previous change. Other variants tween the value
    // in place (above).
    const shown =
      (variant === "compare" || variant === "core") && typeof kpi.previous === "number" ? (
        <FadeSwap
          swapKey={`${String(rawValue)}|${String(kpi.previous)}`}
          className="h-full"
          innerClassName="h-full"
        >
          {card}
        </FadeSwap>
      ) : (
        card
      );
    // Tasks: the flag of a KPI card (the portal only; the builder
    // Preview passes no adapter). `grid` keeps the card stretched.
    const onFlagItem = rowFlags?.onFlagItem;
    if (!onFlagItem) return shown;
    const kpiLabel = resolveLocalized(kpi.label as DisplayLabel, lang);
    return (
      <div className="group relative grid h-full">
        {shown}
        <ItemFlag
          onFlagWhole={() =>
            onFlagItem({
              anchorType: "kpi",
              itemLabel: kpiLabel,
              itemId: cfg.item_id,
              shown: { [kpiLabel]: formatCellValue(rawValue, kpi.format) },
            })
          }
        />
      </div>
    );
  };

  const renderDrill = (): ReactNode => {
    if (!activeDrillId) return null;
    const drill = config.drills?.find((d) => d.drill_id === activeDrillId);
    return (
      <DrillSection
        drillId={activeDrillId}
        title={drill?.title ?? null}
        description={drill?.description ?? null}
        data={drillCache[activeDrillId] ?? null}
        loading={drillLoading}
        error={drillError}
        chartConfigs={drill?.charts ?? config.charts}
        onClose={closeDrill}
      />
    );
  };

  /** GridCell props that don't vary per item (all referentially stable).
   *  Block refresh/error flags are NOT here (review #56 #7): each cell
   *  receives only ITS OWN block's primitives via `blockFor` below, so
   *  a flag flip on one block never breaks the memo of another block's
   *  cells. */
  const cellCommon = {
    activeFilters,
    queryFilters,
    data,
    instanceKey,
    onCrossFilter,
    crossFilterKeys,
    onChartAction: runChartAction,
    // Dashboard-level bands scorecard blocks inherit. `config.thresholds`
    // is a stable ref per render, so it rides in `cellCommon` without
    // breaking the memo cascade (regla 9).
    dashboardThresholds: config.thresholds,
    onToggleRowDrill: toggleRowDrill,
    onCloseRowDrill: closeRowDrill,
    onTableViewStateChange: setTableState,
    onSelectItem,
    // Saved-views portfolio (DSA) — stable refs, memo-safe.
    filters: config.filters,
    onOpenSavedView: openSavedView,
    onSaveView,
    savedViewsRevision,
  } as const;

  /** Dibuja el KPI `idx` para una celda del grid (`in_grid`). Usa la
   *  MISMA `renderCard` que la tira: cambia donde se dibuja, no que. */
  const renderKpiCell = (idx: number): JSX.Element | null => {
    const cfg = (config.kpis ?? [])[idx];
    const kpi = kpis[idx];
    // Todavia sin valor (la tanda no llego): la celda queda vacia en
    // vez de dibujar una tarjeta sin numero.
    if (cfg == null || kpi == null) return null;
    return (
      <div className="h-full">
        {wrapSelectable(cfg.section_id ?? "", cfg.item_id, renderCard(kpi, cfg, idx))}
      </div>
    );
  };

  /** THIS item's block state — tables self-manage (idle values). */
  const blockFor = (kind: GridItemSpec["kind"]): NormalizedBlockState =>
    kind === "chart" || kind === "kpi"
      ? kind === "kpi"
        ? kpisBlock
        : chartsBlock
      : kind === "text"
        ? textsBlock
        : IDLE_BLOCK;

  // ── Uniform grid (desktop ≥768px) ──
  // Explicit placement from the pure simulator (`grid-layout.ts`):
  // sparse packing in authoring order + the vertical stretch rule.
  // Emitted as INLINE grid styles (never dynamic Tailwind classes —
  // the JIT gotcha). A row drill is a STATE OF THE CELL: the table's
  // cell swaps its content for `RowDrillPanel` (inside DashboardTable)
  // — the grid is ONE container that never re-simulates on open/close,
  // no segment split, neighbors don't move a pixel. Cells render in
  // visual (row-major) order so DOM/tab order follows the layout
  // (review #10; the forward-only cursor already emits this order —
  // the sort makes the invariant explicit).
  const renderDesktopGrid = (layout: SectionLayout): ReactNode => {
    const { items, byKey } = layout;
    const ordered = [...items].sort(byVisualOrder(byKey));
    return (
      <div style={GRID_CONTAINER_STYLE}>
        {ordered.map((it) => {
          const p = byKey.get(it.key)!;
          const blk = blockFor(it.kind);
          return (
            <GridCell
              key={it.key}
              sectionId={layout.bucket.id}
              item={it}
              desktop
              colStart={p.colStart}
              colSpan={p.colSpan}
              relRowStart={p.rowStart}
              rowSpan={p.rowSpan}
              px={cellPx(p.rowSpan)}
              swapCfg={
                it.kind === "chart" && it.chart?.cfg.cross_filter_key
                  ? swapEntries.get(it.chart.cfg.cross_filter_key)?.cfg
                  : undefined
              }
              swapData={
                it.kind === "chart" && it.chart?.cfg.cross_filter_key
                  ? (() => {
                      const sw = swapEntries.get(it.chart.cfg.cross_filter_key!);
                      return sw ? charts[sw.idx] : undefined;
                    })()
                  : undefined
              }
              // Solo a las celdas KPI: `renderKpiCell` es una funcion
              // nueva en cada render, asi que pasarsela a TODAS rompe
              // el memo de GridCell y un settle de textos re-renderea
              // los charts. Lo cazo el test de #56 #7.
              renderKpi={it.kind === "kpi" ? renderKpiCell : undefined}
              drillState={rowDrills[it.key] ?? null}
              tableViewState={tableStates[it.key] ?? null}
              refreshing={blk.refreshing}
              blockError={blk.error}
              onRetryBlock={blk.onRetry}
              inspectorSelected={
                selectedItemId != null && selectedItemId === it.itemId
              }
              {...cellCommon}
            />
          );
        })}
      </div>
    );
  };

  // ── Mobile stack (<768px) ──
  // No simulator: one column in authoring order. Charts/mini-tables
  // keep their authored height (fixed wrapper + cellPx plumbed);
  // tables render at natural height CAPPED at their cell px with
  // internal scroll (coherent with desktop); texts stay intrinsic with
  // the authored height as a floor (review #11). Row drills use the
  // SAME in-place swap inside the table block (uncontrolled
  // DashboardTable), bounded by the same capPx.
  const renderMobileStack = (layout: SectionLayout): ReactNode => (
    <div className="flex flex-col gap-4">
      {layout.items.map((it) => {
        const blk = blockFor(it.kind);
        return (
          <GridCell
            key={it.key}
            sectionId={layout.bucket.id}
            item={it}
            desktop={false}
            px={cellPx(it.heightUnits)}
            swapCfg={
              it.kind === "chart" && it.chart?.cfg.cross_filter_key
                ? swapEntries.get(it.chart.cfg.cross_filter_key)?.cfg
                : undefined
            }
            swapData={
              it.kind === "chart" && it.chart?.cfg.cross_filter_key
                ? (() => {
                    const sw = swapEntries.get(it.chart.cfg.cross_filter_key!);
                    return sw ? charts[sw.idx] : undefined;
                  })()
                : undefined
            }
            renderKpi={it.kind === "kpi" ? renderKpiCell : undefined}
            drillState={null}
            tableViewState={tableStates[it.key] ?? null}
            refreshing={blk.refreshing}
            blockError={blk.error}
            onRetryBlock={blk.onRetry}
            inspectorSelected={selectedItemId != null && selectedItemId === it.itemId}
            {...cellCommon}
          />
        );
      })}
    </div>
  );

  // Glossary (DSA 1.16): a button on the right, above the sections.
  // Encabezado compacto H1c (Pablo, 2026-10-26): the scope strip that
  // shared this row ("Vista: Por SKU · Período: 30d…", DSA 1.4 / D36) is
  // no longer drawn, because the filter bar right above already shows
  // every one of those values; ``scope_strip`` is retired from configs
  // (the engine refuses it). ``ScopeStrip`` itself stays for the
  // read-only chips of a send capture.
  const glossary =
    !hideGlossary && config.glossary && config.glossary.sections?.length ? config.glossary : null;
  const contextRow = glossary ? (
    <div data-dashboard-context="" className="mb-3 flex items-center justify-end gap-2">
      <GlossaryDrawer glossary={glossary} />
    </div>
  ) : null;

  return (
    <RowFlagProvider value={rowFlags}>
      {contextRow}
      {layouts.map((layout) => {
        const b = layout.bucket;
        const kpiEntries = b.kpis
          .map(({ cfg, idx }) => ({ cfg, idx, kpi: kpis[idx] }))
          .filter(
            (e): e is { cfg: KpiConfig; idx: number; kpi: Kpi } => e.kpi != null,
          );
        // A KPI-only section whose fetch FAILED on first load still
        // renders (its error card) — review #56 #2.
        const kpisFailedEmpty =
          b.kpis.length > 0 && kpiEntries.length === 0 && kpisBlock.error != null;
        const hasItems =
          kpiEntries.length + layout.items.length > 0 ||
          layout.error != null ||
          kpisFailedEmpty;
        if (!hasItems) return null;
        const headerStr = resolveLocalized(b.title as DisplayLabel, lang);
        const hasHeader = headerStr.trim().length > 0;
        // Nuevo look (iteración 2, 2026-10-24): a titled section is a
        // plain block on the page canvas — the header above, the items
        // below — not a grey card around them.
        const wrapClass = hasHeader ? "min-w-0" : "";
        const innerPad = "space-y-2";
        return (
          <Fragment key={b.id || "__no_section__"}>
            {/* `id` = the section id from the config, so a KPI action band
                can link to it (`href: "#sec_retailers"`). */}
            <div className={wrapClass} id={b.id || undefined}>
              {hasHeader && onSelectSection && b.id ? (
                // Builder inspector: the section title opens the
                // SectionInspector, by click or keyboard. The prop was
                // declared and passed but never read, so a click fell
                // through to the canvas and cleared the selection
                // (QA F09, 2026-10-26).
                <div
                  role="button"
                  tabIndex={0}
                  data-section-select={b.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectSection(b.id!);
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter" && e.key !== " ") return;
                    e.preventDefault();
                    e.stopPropagation();
                    onSelectSection(b.id!);
                  }}
                  className="cursor-pointer rounded-xl hover:ring-2 hover:ring-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <SectionHeader
                    title={headerStr}
                    subtitle={resolveLocalized(b.subtitle as DisplayLabel, lang)}
                    info={resolveLocalized(b.info as DisplayLabel, lang) || undefined}
                  />
                </div>
              ) : hasHeader ? (
                <SectionHeader
                  title={headerStr}
                  subtitle={resolveLocalized(b.subtitle as DisplayLabel, lang)}
                  info={resolveLocalized(b.info as DisplayLabel, lang) || undefined}
                />
              ) : null}
              <div className={innerPad}>
                {/* Review #56 #2 — KPI fetch failed with nothing to
                    keep (first load): the strip's slot renders an
                    error card with a KPIs-only retry. */}
                {b.kpis.length > 0 &&
                  kpiEntries.length === 0 &&
                  kpisBlock.error != null && (
                    <BlockErrorCard
                      message={kpisBlock.error}
                      onRetry={kpisBlock.onRetry}
                    />
                  )}
                {kpiEntries.length > 0 && (
                  // SWR (PR-B): the KPI strip stays mounted + dims
                  // while the host's KPI fetch is in flight. A failed
                  // refresh keeps the stale strip visible but MARKED
                  // (review #56 #2).
                  <BlockStale
                    error={kpisBlock.error}
                    onRetry={kpisBlock.onRetry}
                  >
                  <RefreshOverlay refreshing={kpisBlock.refreshing}>
                  {(() => {
                    const kpiCount = kpiEntries.length;
                    const firstWidth = kpiEntries[0]?.cfg.width ?? null;
                    const allSameWidth =
                      firstWidth != null &&
                      kpiEntries.every((k) => (k.cfg.width ?? null) === firstWidth);
                    const uniformCls = UNIFORM_MD[kpiCount];
                    if (allSameWidth && uniformCls) {
                      return (
                        <div
                          // Equal-height tiles WITHIN a row: `items-stretch`
                          // hands every cell the height of the tallest
                          // card in ITS row. Not `auto-rows: 1fr` — that
                          // equalised every row of the strip to the
                          // tallest row anywhere, so a second row of
                          // plain count cards under a row of `core` cards
                          // (value + delta + formula + action band) sat in
                          // ~300px of air with 46px of content.
                          className={`grid grid-cols-2 ${uniformCls} items-stretch gap-x-3 gap-y-2`}
                        >
                          {kpiEntries.map(({ kpi, cfg, idx }) => (
                            <div
                              className="h-full"
                              key={`${resolveLocalized(kpi.label as DisplayLabel, lang)}-${idx}`}
                            >
                              {wrapSelectable(b.id, cfg.item_id, renderCard(kpi, cfg, idx))}
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return (
                      <div
                        // Same rule as the uniform path: equal height
                        // per row, never across rows.
                        className="grid grid-cols-1 md:grid-cols-12 items-stretch gap-x-3 gap-y-2"
                      >
                        {kpiEntries.map(({ kpi, cfg, idx }) => {
                          const w = Math.max(1, Math.min(12, cfg.width ?? 3));
                          return (
                            <div
                              key={`${resolveLocalized(kpi.label as DisplayLabel, lang)}-${idx}`}
                              className={`${spanClass(w)} h-full`}
                            >
                              {wrapSelectable(b.id, cfg.item_id, renderCard(kpi, cfg, idx))}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  </RefreshOverlay>
                  </BlockStale>
                )}
                {layout.error != null ? (
                  <MessageCard tone="error">
                    Invalid layout config: {layout.error}
                  </MessageCard>
                ) : (
                  layout.items.length > 0 &&
                  (isDesktop ? renderDesktopGrid(layout) : renderMobileStack(layout))
                )}
              </div>
            </div>
            {sectionOwnsActiveDrill(b) && renderDrill()}
          </Fragment>
        );
      })}

      {/* Legacy singular table (dashboard_explorer) — only when no named
          tables AND the singular table actually declares visible columns
          (scorecard reports like gating_executive have an empty singular
          table that the portal's old render suppressed). The condition
          lives in the shared bucketing so the skeleton mirrors it
          (review #7a). */}
      {legacyTable && (
        <DashboardTable
          tableConfig={{ ...legacyTable, id: MAIN_TABLE_ID }}
          activeFilters={activeFilters}
          queryFilters={queryFilters}
          data={data}
          exportName={instanceKey}
          onCrossFilter={onCrossFilter}
          crossFilterKeys={crossFilterKeys}
        />
      )}

      {/* Drill fallback — drill open but no section owns its trigger KPI. */}
      {activeDrillId &&
        !layouts.some((l) => sectionOwnsActiveDrill(l.bucket)) &&
        renderDrill()}
    </RowFlagProvider>
  );
}
